import { nextPaint } from '_shared_/js/utils/afterNextPaint';
import { withTimeout } from '_shared_/js/utils/withTimeout';
import { isExternalUrl } from '_shared_/js/utils/anchorLinks';
import { EpubNavigator } from '@readium/navigator';
import { sMLWithRequest } from '@readium/helpers';
import { Link, Locator, LocatorLocations, LocatorText, Manifest, Publication } from '@readium/shared';
import { ReadiumFadeAnimator } from '../ReadiumFadeAnimator.js';
import { PagebreakTracker, collectPagebreakMarkers, pagebreakPageNumber } from '../PagebreakTracker.js';
import { findReportedAnchor, resolveFootnoteClick } from '../FootnoteResolver.js';

import {
    API_FONT_SIZE_BASE,
    API_FONT_SIZE_MAX,
    API_FONT_SIZE_MIN,
    FONT_SIZE_DEFAULT,
    FONT_SIZE_MAX,
    FONT_SIZE_MIN,
    FONT_SIZE_STEP,
    FOOTNOTE_CONTENT_STYLES,
    FRAME_TEXT_SIZE_CSS,
    LINE_LENGTH_MAXIMAL,
    LINE_LENGTH_MINIMAL,
    LINE_LENGTH_OPTIMAL,
    MARGINS_DEFAULT,
    PAGEBREAK_DEBUG_CSS,
    getAccessibleFontsCss,
    getEffectiveZoom,
    getLineLengths,
    getPagedFlowCss,
    getParagraphPreferences,
    getSpreadGutter,
    getZoomCompensatedPadding,
} from './readium2-styles.js';

import { sendBreadcrumb } from './sentryBreadcrumb.js';
import {
    FRAMES_MEMBER,
    attachedFrames,
    listenToFrame,
    readFrameDocument,
    readFrameResource,
    readFrameWindow,
    sendToFrame,
} from './frameAccess.js';
import {
    GO_TEXT,
    beginsOnReadingScreen,
    captureReadingAnchor,
    findFirstVisibleElement,
    firstTextRangeAfter,
    intersectsReadingViewport,
    locateReadingAnchor,
    readingAnchorPayload,
    scrollToReadingAnchor,
} from './readingAnchor.js';
import { ReadingAnchorKeeper } from './ReadingAnchorKeeper.js';
import { renderPagebreakLabels } from './pagebreakDebugOverlay.js';
import { buildPrintedPageLocators } from './printedPageLocators.js';
import { applyCoverFrameStyles } from './coverFrame.js';
import { getFrameAllowedDomains } from './frameAllowedDomains.js';
import { withRenditionSpread } from './renditionSpread.js';
import { parseCFI, toInitialLocator } from './locatorHelpers.js';
import { resolveInitialLocator } from './initialLocatorResolver.js';
import matchBookmarkInViewport from './matchBookmarkInViewport.js';
import * as Sentry from '@sentry/browser';
import { rebaseCssUrls } from './cssInliner.js';
import { LazyCssCache } from './LazyCssCache.js';
import { adaptPublisherCssInDocument } from './publisherCssCompat.js';
import { setupIdpfFontDeobfuscation } from './fontDeobfuscator.js';
import { DecryptingHttpFetcher } from './DecryptingHttpFetcher.js';
import { HighlightManager } from './HighlightManager.js';
import { findRangeParentElements, readRangeText } from './rangeGeometry.js';
import { readDevConfig } from '../../../../TestState/devConfig.js';
import {
    buildEpubTocIndex,
    epubResourcesMatch,
    epubTocLocationsEqual,
    normalizeEpubResource,
    resolveEpubTocLocation,
} from './epubTableOfContents.js';
import { findEpubTocTarget, listStartedEpubTocAnchorKeys, locateEpubTocAnchors } from './epubTocAnchors.js';

// Whether Readium2 will apply the font-size setting as CSS `zoom` on the
// content body. Mirrors the navigator's own checks: `deprecatedFontSize`
// falls back to font-size when the browser lacks `zoom` support, and the iOS
// mobile patch uses `-webkit-text-size-adjust` instead (see the
// `--USER__fontSize` rules in ReadiumCSS-after.css and EpubDefaults in
// @readium/navigator). Pixel paddings are only scaled by the browser in the
// zoom case, so getZoomCompensatedPadding must only divide there.
const IS_ZOOM_BASED_FONT_SIZING = (() => {
    try {
        const supportsZoom = typeof CSS !== 'undefined' && CSS.supports('zoom', '1');
        const isIOSMobile =
            (sMLWithRequest.OS.iOS || sMLWithRequest.OS.iPadOS) && sMLWithRequest.iOSRequest === 'mobile';
        return supportsZoom && !isIOSMobile;
    } catch {
        return false;
    }
})();

// Upper bound for a tracking-suppression window. Progress-bar jumps settle
// in ~1s (landing + within-chapter adjustment); 3s covers slow chapter
// loads while keeping a stuck window (lost ack) short-lived.
const SUPPRESSION_ESCAPE_TIMEOUT_MS = 3000;

// Upper bound for each step of returning to a reading anchor (the frame's
// fonts, the landing). A frame that never answers must not hold every later
// change behind it.
const READING_ANCHOR_TIMEOUT_MS = 2000;
// The go() ack can arrive before Readium snaps the column, so anything that
// reads the landing waits this long after it.
const COLUMN_SNAP_SETTLE_MS = 350;

// The navigator members _serializeNavigatorMutations patches and frameAccess
// reads.
const NAVIGATOR_SURFACES = [FRAMES_MEMBER, 'submitPreferences', 'resizeHandler'];
// Preferences that recolour the page or pad it below the text. A submit made of
// these alone leaves the first visible line where it is, so it skips the
// re-layout round trip. A fast path, not a correctness rule: an unknown key
// counts as moving text and costs one needless landing on the same words.
const POSITION_NEUTRAL_PREFERENCES = new Set([
    'backgroundColor',
    'textColor',
    'linkColor',
    'selectionBackgroundColor',
    'selectionTextColor',
    'scrollPaddingBottom',
]);

/**
 * EpubViewer - A comprehensive EPUB reader implementation using Readium 2
 *
 * This class provides a complete EPUB reading experience with support for:
 * - Content decryption
 * - Multiple layout modes (single, double, scroll)
 * - Text selection and highlighting
 * - Chapter navigation and boundaries detection
 * - Theme management
 * - Keyboard and touch interactions
 * - Preview mode restrictions
 *
 * Built on top of Readium 2 Navigator and follows Readium 2 architecture patterns.
 * Handles both encrypted and unencrypted EPUB content through a custom fetcher system.
 *
 * @class EpubViewer
 */
/** `Locator` splits a `#fragment` off its href into `locations.fragments`. */
function hrefWithFragment(locator) {
    const [fragment] = locator.locations?.fragments ?? [];
    return fragment ? `${locator.href}#${fragment}` : locator.href;
}

/**
 * Readium forwards only these schemes from a click, but its go() forwards any
 * locator missing from the reading order, so a nav document's javascript: or
 * intent: entry must not reach the host either.
 */
const HOST_LINK_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'tel:']);

function hostCanOpen(href) {
    return HOST_LINK_PROTOCOLS.has(new URL(href).protocol);
}

export class EpubViewer {
    /**
     * Creates an instance of EpubViewer with callback configurations
     *
     * @param {Object} config - Configuration object for the EPUB viewer
     */
    constructor({
        isPreviewMode,
        settings = {},
        scrollClearances = { top: 0, bottom: 0 },
        lastPosition = {},
        onGoNext = () => {},
        onGoPrev = () => {},
        onKeyboardArrowPressed = () => {},
        onEndOfChapter = () => {},
        onStartOfChapter = () => {},
        onChapterChange = () => {},
        onClearSelection = () => {},
        onSelectionInProgress = () => {},
        onToggleUi = () => {},
        onSelectHighlight = () => {},
        onChangePosition = () => {},
        onNoteMarkersChanged = () => {},
        onContentScrolled = () => {},
        onLoadFrame = () => {},
        onNavigationStart = () => {},
        onNavigationEnd = () => {},
        onHighlightCreated = () => {},
        onHighlightUpdated = () => {},
        onHighlightDeleted = () => {},
        onSearchHighlightRendered = () => {},
        onTrackEvent = () => {},
        onSetCurrentHighlight = () => {},
        onFulfillQueryWordsHighlight = () => {},
        onHandleAnonymousSessionSelections = () => {},
        onImageClick = () => {},
        onSpreadRuleKnown = () => {},
        onFootnoteClick = () => {},
        isNavigationEnabled = () => true,
        isKeyboardNavigationEnabled = () => true,
        isMobileOverlayOpen = () => false,
        onCloseMobileOverlay = () => {},
        onOpenLink = () => {},
        onSwipe = () => {},
        onPagebreakConfirmed = () => {},
        onPagebreaksEntered = () => {},
        onCurrentPageChanged = () => {},
        onFixedLayoutPagesChanged = () => {},
        onTrackingSuppression = () => {},
        onTocLocationChanged = () => {},
        pageViewConfirmDelayMs,
    }) {
        this.epubNavigator = null;
        this.publication = null;
        this.lastPosition = lastPosition;
        this.currentFontSize = this.parseInitialFontSize(settings?.zoom);
        // Raw (uncompensated) pixel values of the zoom-dependent paddings.
        // getZoomDependentPreferences() derives the submitted values from
        // these whenever the margins or the font size change.
        this.currentMargins = settings?.margins ?? MARGINS_DEFAULT;
        this.currentScrollPaddingTop = scrollClearances.top;
        this.currentScrollPaddingBottom = scrollClearances.bottom;
        this.settings = this.parseSettings(settings);
        this.preventClick = true;
        this.selection = {};
        this.currentChapterHref = null;
        this.isChapterEnd = false;
        this.isChapterStart = false;
        this.isPreviewMode = isPreviewMode;
        this.onGoNext = onGoNext;
        this.onGoPrev = onGoPrev;
        this.onKeyboardArrowPressed = onKeyboardArrowPressed;
        this.onEndOfChapter = onEndOfChapter;
        this.onStartOfChapter = onStartOfChapter;
        this.onChapterChange = onChapterChange;
        this.onClearSelection = onClearSelection;
        this.onSelectionInProgress = onSelectionInProgress;
        this.onToggleUi = onToggleUi;
        this.onSelectHighlight = onSelectHighlight;
        this.onChangePosition = onChangePosition;
        this.onNoteMarkersChanged = onNoteMarkersChanged;
        this.onContentScrolled = onContentScrolled;
        this.onLoadFrame = onLoadFrame;
        this.onNavigationStart = onNavigationStart;
        this.onNavigationEnd = onNavigationEnd;
        this.onImageClick = onImageClick;
        this.onSpreadRuleKnown = onSpreadRuleKnown;
        this.onFootnoteClick = onFootnoteClick;
        this.isNavigationEnabled = isNavigationEnabled;
        this.isKeyboardNavigationEnabled = isKeyboardNavigationEnabled;
        this.isMobileOverlayOpen = isMobileOverlayOpen;
        this.onCloseMobileOverlay = onCloseMobileOverlay;
        this.onOpenLink = onOpenLink;
        this.onSwipe = onSwipe;
        this._isFxlLoading = () => false;
        this._fadeAnimator = new ReadiumFadeAnimator();

        // Initialize HighlightManager with callbacks
        this.highlightManager = new HighlightManager({
            onHighlightCreated,
            onHighlightUpdated,
            onHighlightDeleted,
            onSearchHighlightRendered,
            onTrackEvent,
            onSetCurrentHighlight,
            onFulfillQueryWordsHighlight,
            onHandleAnonymousSessionSelections,
            onSelectHighlight: this.onSelectHighlight,
            onSelectionInProgress, // @R2 selection popup
            onClearSelection, // @R2 dismiss selection popup
            onMarkersRendered: () => this.notifyNoteMarkerAnchors(),
        });

        this._pagebreakTracker = new PagebreakTracker(onPagebreakConfirmed, {
            confirmDelayMs: pageViewConfirmDelayMs,
            onPagebreaksEntered,
            onCurrentPageChanged,
            getReadingContext: () => this._readingContext(),
        });

        /**
         * Reports the printed pages of the current fixed-layout spread
         * (ascending numbers in the text index's page domain). FXL-only
         * counterpart of the tracker's current-page signal: the tracker
         * never observes FXL frames, but there one spine file IS a page, so
         * its single marker resolves the number exactly — publisher
         * numbering included, which a Readium position cannot reflect.
         */
        this.onFixedLayoutPagesChanged = onFixedLayoutPagesChanged;

        // Dev-only pagebreak overlay. readDevConfig is import-safe: outside
        // standalone dev it returns frozen defaults, so this is always false
        // in production. Reload-mode knob, so the value is fixed for the
        // whole page lifetime.
        this._debugPagebreaks = readDevConfig().epubPagebreaks;

        // Container element reference for viewport manipulation
        this.container = null;

        // Observer that re-collects note-marker anchors on reader layout shifts
        this._containerResizeObserver = null;

        // Orientation media query whose changes leave a text-metrics breadcrumb
        this._orientationQuery = null;
        this._onOrientationChange = null;

        // Flag to suppress animation only during layout changes and sidebar reflow
        this._suppressAnimation = false;

        // Navigator mutations (preference submits, resizes) run one at a time
        // through this chain; see _serializeNavigatorMutations.
        this._navigatorMutations = Promise.resolve();
        this._anchorKeeper = new ReadingAnchorKeeper({
            capture: () => this.captureReadingAnchor(),
            isVisible: anchor => this._isReadingAnchorVisible(anchor),
        });
        this._readingInputDocuments = new Map();

        // Tracking-suppression window state. The viewer signals the
        // analytics listener around programmatic navigation so render-driven
        // page-changes don't read as user turns. Reference-counted: nested
        // windows (a jump inside a reflow) keep suppression up until the last
        // one closes, and the escape timeout guarantees a close even when a
        // navigator ack never arrives.
        this.onTrackingSuppression = onTrackingSuppression;
        this.onTocLocationChanged = onTocLocationChanged;
        this._suppressionDepth = 0;
        this._suppressionEscapeTimer = null;
        this._tocIndex = null;
        this._tocTargetCache = new WeakMap();
        this._lastTocLocation = null;
        this._lastTocResource = '';
    }

    /**
     * Opens a tracking-suppression window. Mode 'silent' drops every
     * page-change in the window (reflows: nothing the user did); mode
     * 'emit-final' collapses the window to a single page-change with the
     * final destination (user jumps: progress bar, TOC, note source).
     */
    beginTrackingSuppression(mode) {
        this._suppressionDepth++;
        if (this._suppressionDepth === 1) {
            this.onTrackingSuppression({ phase: 'begin', mode });
        }
        this._armSuppressionEscape();
    }

    /**
     * Closes the current tracking-suppression window after the navigation
     * settles. Waits two animation frames so the navigator's positionChanged
     * (and the tracker's visibility check) from the final landing are
     * processed inside the window.
     */
    endTrackingSuppression() {
        return nextPaint().then(() => this._closeSuppressionWindow());
    }

    /** @private */
    _closeSuppressionWindow() {
        if (this._suppressionDepth === 0) return;
        this._suppressionDepth--;
        if (this._suppressionDepth > 0) return;

        if (this._suppressionEscapeTimer) {
            clearTimeout(this._suppressionEscapeTimer);
            this._suppressionEscapeTimer = null;
        }
        this.onTrackingSuppression({ phase: 'end' });
    }

    /**
     * Force-closes the window when an ack never arrives (frame died,
     * navigation rejected). Without this, suppression would stick and every
     * subsequent turn would be silently dropped.
     * @private
     */
    _armSuppressionEscape() {
        if (this._suppressionEscapeTimer) {
            clearTimeout(this._suppressionEscapeTimer);
        }
        this._suppressionEscapeTimer = setTimeout(() => {
            this._suppressionEscapeTimer = null;
            if (this._suppressionDepth === 0) return;
            this._suppressionDepth = 0;
            this.onTrackingSuppression({ phase: 'end' });
        }, SUPPRESSION_ESCAPE_TIMEOUT_MS);
    }

    /**
     * Checks whether the last available page is currently visible in the navigator's viewport.
     * Works for FXL (single/double), reflowable, and preview EPUBs by comparing
     * visible position numbers against the last available position.
     * @returns {boolean} True if the last available page is visible
     */
    isLastPageVisible() {
        const viewportPositions = this.epubNavigator?.viewport?.positions;
        const maxVisiblePosition = viewportPositions?.length ? Math.max(...viewportPositions) : 0;
        const lastAvailablePosition =
            this.epubNavigator?.positions?.[this.epubNavigator.positions.length - 1]?.locations?.position;
        return lastAvailablePosition > 0 && maxVisiblePosition >= lastAvailablePosition;
    }

    /**
     * Returns the bookmark (from the given list) anchored within the currently visible page,
     * or null. An EPUB "page" spans a *range* of positions/progression, so a bookmark counts
     * as "on this page" when its progression falls inside the visible range of its resource —
     * not when it merely equals the first visible position. The EPUB viewer owns this check
     * because only it has the navigator viewport (the visible progression range per resource).
     * @param {Array<Object>} bookmarks
     * @returns {Object|null}
     */
    getCurrentPageBookmark(bookmarks = []) {
        return matchBookmarkInViewport(bookmarks, this.epubNavigator?.viewport);
    }

    /**
     * Returns the printed page number at the reading edge, derived from the
     * printed-page markers in the content (see PagebreakTracker). Returns
     * null for fixed-layout EPUBs, books without page markers, or when no
     * numbered page is current — the caller falls back to the Readium
     * position in those cases.
     * @returns {number|null}
     */
    getCurrentPageNumber() {
        // Fixed-layout EPUBs are not pagebreak-tracked (frameLoaded skips
        // observeFrame for FXL); the caller falls back to the Readium position.
        if (this.isFixedLayout()) return null;

        // The tracker owns the frame registry and the reading context (fed
        // through the getReadingContext provider), so there is nothing to
        // pass: pull and push paths share the same walk.
        return this._pagebreakTracker?.getCurrentPageNumber() ?? null;
    }

    /**
     * The printed pages on screen (see PagebreakTracker). Empty for
     * fixed-layout EPUBs, which are not pagebreak-tracked, and for books
     * without page markers.
     * @returns {number[]}
     */
    getVisiblePageNumbers() {
        if (this.isFixedLayout()) return [];

        return this._pagebreakTracker?.getVisiblePageNumbers() ?? [];
    }

    /**
     * Reading context for the pagebreak tracker: which axis the reading edge
     * lives on and how far Readium insets the content from the viewport
     * border (page gutter when paginated, top scroll padding in scroll
     * mode). These base values are painted at ~1:1 by the zoom compensation
     * (see getZoomDependentPreferences), so they match getBoundingClientRect.
     * Provided as a live callback because layout and margins change at
     * runtime.
     * @private
     * @returns {{ scroll: boolean, edgeStart: number }}
     */
    _readingContext() {
        const scroll = !!this.scrollPageMode;
        return { scroll, edgeStart: scroll ? this.currentScrollPaddingTop : this.getEffectivePageGutter() };
    }

    /**
     * Injects the dev-only pagebreak badges (pagebreaks=1) into every live chapter frame.
     *
     * Once per frame is all it takes — the badges live inside the markers, so the layout
     * engine carries them through page turns, and each one's side is decided against its
     * own column, which the chapter's layout fixes. But "once per frame" is not the same
     * as "once per frameLoaded": that event fires only for `currentFrames[0]`, so
     * pool-preloaded chapters and the second frame of a spread never reach it, which
     * showed up as every other page missing its badge. Hence the enumeration, and the
     * call on positionChanged as well — the injection is idempotent, so a frame already
     * done costs one query and no DOM writes.
     * @private
     */
    _renderPagebreakLabels() {
        if (!this._debugPagebreaks) return;
        this._contentFrameWindows().forEach(wnd => renderPagebreakLabels(wnd));
    }

    /**
     * The reading context from the frame edge, header and gutter included:
     * where Readium's own first-visible walk starts. Reading anchors use
     * _readingContext() instead, which starts where the reader's view does.
     * @returns {{ scroll: boolean, edgeStart: number }}
     */
    _frameEdgeContext() {
        return { scroll: !!this.scrollPageMode, edgeStart: 0 };
    }

    /** @returns {Window|null} the window of the chapter frame in front */
    _currentFrameWindow() {
        return readFrameWindow(this._currentFrame());
    }

    /** @returns {object|undefined} the chapter frame in front */
    _currentFrame() {
        return attachedFrames(this.epubNavigator)[0];
    }

    /** @returns {Window[]} the windows of the chapter frames the pool still holds */
    _attachedFrameWindows() {
        return attachedFrames(this.epubNavigator).map(readFrameWindow).filter(Boolean);
    }

    /** @returns {Document[]} the documents of the chapter frames that still answer one */
    _attachedFrameDocuments() {
        return attachedFrames(this.epubNavigator).map(readFrameDocument).filter(Boolean);
    }

    /** @returns {boolean} whether the anchor names the resource the frame is showing */
    _anchorNamesCurrentResource(anchor) {
        return epubResourcesMatch(anchor?.href, this.epubNavigator?.currentLocation?.href);
    }

    /**
     * Takes a reading anchor for what the reader sees now (see readingAnchor.js).
     * @returns {Locator|null} null for fixed layouts, whose pages are their own anchor, and before the frame exists
     */
    captureReadingAnchor() {
        try {
            if (this.isFixedLayout()) return null;
            return captureReadingAnchor(
                this._currentFrameWindow(),
                this.epubNavigator?.currentLocation,
                this._readingContext()
            );
        } catch (e) {
            sendBreadcrumb('layout', 'reading_anchor_capture_failed', { error: e?.message }, 'warning');
            return null;
        }
    }

    markReflowStart() {
        this._anchorKeeper.freeze();
    }

    _observeReadingInput() {
        if (this._anchorKeeper.isDisposed) return;
        const documents = this._attachedFrameDocuments();
        for (const [doc, listener] of this._readingInputDocuments) {
            if (documents.includes(doc)) continue;
            for (const event of ['wheel', 'touchmove', 'keydown']) doc.removeEventListener(event, listener, true);
            this._readingInputDocuments.delete(doc);
        }
        for (const doc of documents) {
            if (this._readingInputDocuments.has(doc)) continue;
            const listener = event => {
                if (!this.scrollPageMode) return;
                if (
                    event.type === 'keydown' &&
                    !['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key)
                )
                    return;
                this._anchorKeeper.noteUserNavigation();
            };
            for (const event of ['wheel', 'touchmove', 'keydown'])
                doc.addEventListener(event, listener, { capture: true, passive: true });
            this._readingInputDocuments.set(doc, listener);
        }
    }

    /**
     * Returns the reader to an anchor after a re-layout. The caller holds the
     * silent tracking window: a reflow is not a page turn.
     * @param {Locator|null} anchor - from captureReadingAnchor
     * @returns {Promise<boolean>} whether the reader landed on the anchor
     */
    restoreReadingAnchor(anchor) {
        const nav = this.epubNavigator;
        if (!anchor || !nav) return Promise.resolve(false);

        // A fixed-layout resize holds a spine slide (see _fixedLayoutResizeTarget),
        // a resource to navigate to rather than a place inside one document.
        if (this.isFixedLayout()) {
            return withTimeout(
                new Promise(resolve => nav.go(anchor, false, resolve)),
                READING_ANCHOR_TIMEOUT_MS,
                false
            );
        }

        // An anchor from another chapter is not this frame's to land: its selector
        // resolves against whichever document is on screen, matching the wrong element.
        if (!this._anchorNamesCurrentResource(anchor)) return Promise.resolve(false);

        // ScrollSnapper's go_text centres its target, so scroll mode lands the
        // anchor itself at the reading edge. ColumnSnapper already snaps to it.
        const located = this.scrollPageMode ? this._locateReadingAnchor(anchor) : null;
        if (located) {
            scrollToReadingAnchor(located.wnd, located.target, this._readingContext().edgeStart);
            return Promise.resolve(true);
        }

        const payload = readingAnchorPayload(anchor);
        if (!payload) return Promise.resolve(false);

        // Sent to the frame rather than through nav.go(), which reloads the
        // resource and rebuilds the frame the landing then reads.
        return withTimeout(sendToFrame(this._currentFrame(), GO_TEXT, payload), READING_ANCHOR_TIMEOUT_MS, false);
    }

    /**
     * Resolves once the current frame's text is laid out in the fonts it will
     * keep, or after the anchor timeout. A frame rebuilt for a new layout
     * lays its text out in fallback metrics until the publisher's fonts
     * arrive, which moves every line after them.
     *
     * One wait is not enough. A submitted preference reaches the frame
     * asynchronously, and a face can pull in another, so faces keep
     * starting after fonts.ready has resolved.
     * @returns {Promise<void>}
     */
    async _settleFrameFonts() {
        const deadline = Date.now() + READING_ANCHOR_TIMEOUT_MS;
        const timeLeft = () => deadline - Date.now();
        while (timeLeft() > 0) {
            const doc = this._currentFrameWindow()?.document;
            if (!doc?.fonts) return;
            // Flush the style the frame has pending, so the wait answers
            // for the layout as it stands.
            if (doc.body) void doc.body.offsetHeight;
            await withTimeout(doc.fonts.ready, timeLeft());
            // A backgrounded frame paints nothing, so the deadline bounds
            // this too: the reflow it holds open must not outlive it.
            await withTimeout(nextPaint(), timeLeft());
            if (doc.fonts.status !== 'loading') return;
        }
    }

    /**
     * Finds an anchor in the current frame. Callers own the check that the
     * anchor names the resource that frame is showing.
     * @returns {{ wnd: Window, target: Range|Element }|null} null when the anchor is gone
     */
    _locateReadingAnchor(anchor) {
        const wnd = this._currentFrameWindow();
        const doc = wnd?.document;
        if (!doc) return null;
        const target = locateReadingAnchor(doc, anchor);
        return target ? { wnd, target } : null;
    }

    /** @returns {boolean} whether the anchor's words (or element) are within the current viewport */
    _isReadingAnchorVisible(anchor) {
        try {
            if (!this._anchorNamesCurrentResource(anchor)) return false;
            const located = this._locateReadingAnchor(anchor);
            return (
                !!located &&
                intersectsReadingViewport(located.wnd, located.target.getBoundingClientRect(), this._readingContext())
            );
        } catch {
            return false;
        }
    }

    /**
     * Parses the initial font size from stored settings.
     * Handles both API format (90-480) and REM format (0.75-4).
     * @param {number|null|undefined} zoom - Stored zoom value
     * @returns {number} Font size in rem
     */
    parseInitialFontSize(zoom) {
        if (typeof zoom !== 'number' || zoom <= 0) {
            return FONT_SIZE_DEFAULT;
        }

        if (zoom >= API_FONT_SIZE_MIN && zoom <= API_FONT_SIZE_MAX) {
            return zoom / API_FONT_SIZE_BASE;
        }

        if (zoom >= FONT_SIZE_MIN && zoom <= FONT_SIZE_MAX) {
            return zoom;
        }

        return FONT_SIZE_DEFAULT;
    }

    get scrollPageMode() {
        // The navigator's applied settings win once it exists: `this.settings`
        // is the construction-time snapshot and does not follow layout changes.
        return this.epubNavigator?.settings?.scroll ?? this.settings?.scroll;
    }

    /**
     * Two columns rendered. The navigator reports the count it paginated,
     * which drops to one when two do not fit the reader, not the count asked for.
     */
    get doublePageMode() {
        return (this.epubNavigator?.settings?.columnCount ?? this.settings?.columnCount) === 2;
    }

    /**
     * Readium layout of the publication: fixed, reflowable or scrolled (which
     * also covers vertical-writing books, whose gutter is vertical padding).
     * The navigator answers once it exists, its own reading of the manifest
     * before that.
     */
    _readiumLayout() {
        if (this.epubNavigator) return this.epubNavigator.layout;
        if (!this.publication) return 'reflowable';
        return EpubNavigator.determineLayout(this.publication, !!this.settings?.scroll);
    }

    _isReflowableSpread() {
        return this._readiumLayout() === 'reflowable' && this.doublePageMode && !this.scrollPageMode;
    }

    /**
     * EPUB layout configurations for different reading modes
     */
    epubLayouts = {
        single: {
            scroll: 'auto',
            syntheticSpread: 'single',
        },
        scroll: {
            scroll: 'scroll-doc',
            syntheticSpread: 'auto',
        },
        double: {
            scroll: 'auto',
            syntheticSpread: 'auto',
        },
    };

    /**
     * Gives the active content frame a stable, textual accessible name.
     *
     * The document title describes the full frame even when one XHTML file
     * contains several table-of-contents destinations.
     */
    setFrameTitle(frameWindow) {
        const iframeElement = frameWindow.frameElement;

        if (!iframeElement) {
            return;
        }

        const documentTitle = frameWindow.document.title?.trim();

        if (documentTitle) {
            iframeElement.setAttribute('title', documentTitle);

            return;
        }

        const metadata = this.publication?.metadata;
        const publicationLanguage = Array.isArray(metadata?.languages) ? metadata.languages[0] : metadata?.languages;
        const publicationTitle =
            typeof metadata?.title === 'string'
                ? metadata.title
                : metadata?.title?.getTranslation?.(publicationLanguage);
        const frameTitle =
            typeof publicationTitle === 'string' && publicationTitle.trim() ? publicationTitle.trim() : 'EPUB content';

        iframeElement.setAttribute('title', frameTitle);
    }

    /**
     * Readium 2 Navigator event listeners configuration
     */
    listeners = {
        frameLoaded: async _wnd => {
            this.setFrameTitle(_wnd);
            this._observeReadingInput();

            // WCAG 3.1.2: Set content language from publication metadata.
            const pubLang = this.publication.metadata?.languages;
            if (pubLang) {
                const lang = Array.isArray(pubLang) ? pubLang[0] : pubLang;
                _wnd.document.documentElement.lang = lang;
            }

            // Guard: ensure the css-selector-generator injectable is available.
            // On the very first page load a race condition can leave the injectable
            // uninitialised, causing "getCssSelector is not a function" on click.
            if (!_wnd._readium_cssSelectorGenerator) {
                _wnd._readium_cssSelectorGenerator = {
                    getCssSelector: el => {
                        try {
                            return el?.tagName?.toLowerCase() || '';
                        } catch {
                            return '';
                        }
                    },
                };
            }

            // Blank out the content when the user tries to print the iframe directly
            const head = _wnd.document.head;
            const printProtectionStyle = _wnd.document.createElement('style');
            printProtectionStyle.innerText = `@media print { * { display: none !important; } }`;
            head.appendChild(printProtectionStyle);

            this._installFrameLayers(_wnd);

            _wnd.document.addEventListener('contextmenu', e => {
                this.preventContextMenu(e);
            });

            _wnd.document.body.style.webkitTouchCallout = 'none';
            _wnd.document.querySelectorAll('img').forEach(img => img.setAttribute('draggable', 'false'));

            // R2's ColumnSnapper clears selection on touchmove. Block it when selection exists.
            if (!this.scrollPageMode) {
                _wnd.document.addEventListener(
                    'touchmove',
                    e => {
                        const selection = _wnd.getSelection();
                        if (selection && selection.toString().length > 0) {
                            e.stopPropagation();
                        }
                    },
                    { capture: true, passive: false }
                );
            }

            _wnd.addEventListener('beforeprint', () => {
                const iframe = _wnd.document;
                if (iframe.getSelection) {
                    iframe.getSelection().removeAllRanges();
                } else if (iframe.selection) {
                    iframe.selection.empty();
                }
            });

            // Cover / first frame: the CSS inlining step (inlineCssInHtml) may
            // succeed at string level, but R2's DOMParser + injectForDocument
            // pipeline can strip or fail to retain our <style> tags for certain
            // frames (e.g. the cover which uses a different XML namespace).
            // When this happens, original <link href="../stylesheet.css"> tags
            // remain — they can't resolve from blob: URLs and produce no styles.
            // → We detect non-blob <link> stylesheet tags and inject the CSS
            // from our decryptedCssCache directly into the DOM.
            try {
                const doc = _wnd.document;

                // Determine the XHTML item href from the <base> tag Readium injects
                const baseEl = doc.querySelector('base[data-readium]');
                const xhtmlDir = this._dirFromBaseHref(baseEl?.href);

                // Inject CSS for broken (non-blob) <link> stylesheet tags
                const cache = this.lazyCssCache;
                const links = [...doc.querySelectorAll('link[rel="stylesheet"]')];
                const recovered = [];
                for (const link of links) {
                    const href = link.getAttribute('href') || '';
                    // Blob links are R2's own CSS — leave them alone
                    if (href.startsWith('blob:')) continue;
                    if (!cache) continue;
                    // This is an EPUB CSS link that wasn't inlined — try to inject from cache
                    const result = await cache.get(href, xhtmlDir ?? '');
                    if (result) {
                        let css = result.css;
                        // Rebase url() references for the XHTML context
                        if (xhtmlDir !== null) {
                            css = rebaseCssUrls(css, result.cacheKey, xhtmlDir);
                        }
                        const style = doc.createElement('style');
                        style.setAttribute('type', 'text/css');
                        style.setAttribute('data-original-href', href);
                        style.textContent = css;
                        link.parentNode.replaceChild(style, link);
                        recovered.push(href);
                    }
                }
                // Legacy (v1) publications are fetched without the decrypting
                // resource, so their <style> blocks and style attributes never
                // went through the string pipeline. Adapt them in the DOM, the
                // same fallback the links above get. Idempotent on frames that
                // were adapted before parsing.
                adaptPublisherCssInDocument(doc);

                // Leave a trail when the DOM-level fallback patches a
                // stylesheet: the frame reached the reader without the CSS
                // the inlining stage was supposed to embed, so any rendering
                // error reported afterwards should be read in that light. A
                // breadcrumb and not an event of its own — the fallback doing
                // its job is not a failure, and it fires on a large share of
                // the books, which would bury the errors that do need eyes.
                if (recovered.length > 0) {
                    Sentry.addBreadcrumb({
                        category: 'epub.css',
                        level: 'info',
                        message: '[R2] DOM-level CSS fallback activated',
                        data: {
                            frame: doc.location?.href,
                            hrefs: recovered,
                        },
                    });
                }
            } catch (e) {
                console.warn('[R2] Error fixing EPUB CSS in iframe:', e.message);
            }

            try {
                if (this._isCoverFrame(_wnd)) {
                    applyCoverFrameStyles(_wnd.document);
                }
            } catch (e) {
                console.warn('[R2] Error applying cover frame styles:', e.message);
            }

            _wnd.focus();
            this.onLoadFrame(_wnd);
            this.updateCurrentTocLocation(this.getCurrentLocation(), _wnd);
            // Outside the fixed-layout gate below: the debug overlay only needs the markers
            // to exist in the document, and Farfalla injects them for every EPUB, fixed
            // layout included. What the tracker does with them is a separate question.
            this._renderPagebreakLabels();
            if (!this.isFixedLayout()) {
                this._pagebreakTracker?.observeFrame(_wnd);
            } else {
                this._notifyFixedLayoutPrintedPages();
            }
        },
        positionChanged: _locator => {
            this._anchorKeeper.notePositionChanged();
            this._observeReadingInput();
            this.onChangePosition(_locator);

            this.handlePositionChange(_locator);
            this.updateCurrentTocLocation(_locator);

            this.notifyNoteMarkerAnchors();

            // Pool-preloaded chapters never pass through frameLoaded (R2
            // fires it only with currentFrames[0]), and a frame must be
            // current before it can receive clicks, so this re-check keeps
            // the frame layers installed ahead of the first click.
            this._ensureFrameLayers();

            // Same reason as the frame layers above: pool-preloaded frames skip
            // frameLoaded entirely, so they get their badges here instead.
            this._renderPagebreakLabels();

            if (!this.isFixedLayout()) {
                this._pagebreakTracker?.checkVisibility();
            } else {
                this._notifyFixedLayoutPrintedPages();
            }

            window.focus();
        },
        tap: _e => {
            if (this.isHighlightElement(_e.targetElement)) {
                return true;
            }
            this.handleTap(_e);
            return true;
        },
        click: _e => {
            if (this.isHighlightElement(_e.targetElement)) {
                return true;
            }
            this.handleClick(_e);
            return true;
        },
        zoom: _scale => {},
        /**
         * Calls onEndOfChapter / onStartOfChapter rather than the handlers:
         * the callbacks are idempotent, and the handlers latch until the next
         * position tick, which would swallow the re-show after the reader
         * scrolls away from an edge and back within one position.
         */
        scroll: _delta => {
            this.notifyNoteMarkerAnchors();
            this.onContentScrolled();

            const nav = this.epubNavigator;
            if (!nav) return;
            const loc = nav.currentLocator;
            if (!loc?.locations) return;

            const isNotLastPage = loc.locations.position !== nav.positions.length;
            const isNotFirstPage = loc.locations.position !== 1;
            const isAtEnd = nav.isScrollEnd && isNotLastPage;
            const isAtStart = nav.isScrollStart && isNotFirstPage;

            // Scroll mode reports leaving an edge as well as reaching one. The only
            // other reset is a position tick, and with a large font one position
            // spans several screens, so the button would outlast the edge by a long
            // way. Paged mode reports only arriving: there a tick is a page turn.
            if (this.scrollPageMode) {
                this.onEndOfChapter(isAtEnd);
                this.onStartOfChapter(isAtStart);
                return;
            }

            if (isAtEnd) {
                this.onEndOfChapter();
            }
            if (isAtStart) {
                this.onStartOfChapter();
            }
        },
        miscPointer: _amount => {},
        customEvent: (_key, _data) => {},
        /**
         * A link that leaves the publication goes to the host: the reader runs in
         * an iframe or a WebView that opens no windows of its own, and the host
         * is the one that asks the visitor before leaving. An absolute link under
         * the publication's own base URL is navigation, whatever origin serves it.
         */
        handleLocator: seekLocation => {
            const href = hrefWithFragment(seekLocation);

            // A clicked absolute link carries no position, so it is placed by its path.
            const publicationBase = this.publication?.baseURL;
            if (publicationBase && href.startsWith(publicationBase)) {
                this.goToLink(href.substring(publicationBase.length));
                return true;
            }

            if (isExternalUrl(href) && hostCanOpen(href)) {
                this.onOpenLink(href);
                return true;
            }

            const locator = this.epubNavigator.positions[seekLocation.locations.position] ?? null;

            if (locator) {
                this.goToLocator(locator);
                return true;
            }

            console.warn('Unhandled locator', seekLocation);
            return false;
        },
        // @R2 Backup selection handler: R2's native textSelected event fires when the
        // user finishes selecting text inside an iframe. This is a secondary path —
        // the primary path is _setupSelectionListeners (pointerup on iframe docs) in
        // HighlightManager, which fires more reliably across all iframe configurations.
        // Both paths call onSelectionInProgress to show the highlight popup.
        textSelected: _selection => {
            this.handleTextSelection(_selection);
        },
    };

    /**
     * Initialize the EPUB viewer with publication data
     *
     * @async
     * @param {Object} config - Initialization configuration
     * @param {string} config.containerId - CSS selector for the container element
     * @param {string} config.epubUrl - URL or identifier of the EPUB to load
     * @returns {Promise<EpubNavigator>} Promise that resolves to the initialized navigator
     */
    async init({ containerId, epubUrl }) {
        return new Promise(async (resolve, reject) => {
            // ─── IDPF: revoke blob URLs from any previous init() ───────
            // Guards against leaks if init() runs more than once on the
            // same instance (hot-reload, re-open without unmount, retry).
            // Also covers the case where init() runs again after a previous
            // task already settled into this.idpfFontBlobUrls. The in-flight
            // case (previous _idpfFontsTask not yet settled) is handled below
            // when constructing the new task.
            if (this.idpfFontBlobUrls) {
                for (const url of this.idpfFontBlobUrls.values()) {
                    URL.revokeObjectURL(url);
                }
                this.idpfFontBlobUrls = null;
            }
            // ───────────────────────────────────────────────────────────

            const currentURL = new URL(window.location.href);
            let book = epubUrl;
            let publicationURL = '';
            if (currentURL.searchParams.has('book')) {
                book = currentURL.searchParams.get('book');
            }
            const hostOnly = u => {
                try {
                    return new URL(u).host;
                } catch {
                    return null;
                }
            };
            sendBreadcrumb('init', 'init_start', { epubUrl: hostOnly(book) });
            if (book.startsWith('http://') || book.startsWith('https://') || book.startsWith('file://')) {
                publicationURL = book;
                if (!book.endsWith('manifest.json') && !book.endsWith('/')) publicationURL += '/';
            } else publicationURL = `${currentURL.origin}/${book}/`;
            sendBreadcrumb('init', 'publication_url_resolved', { epubUrl: hostOnly(publicationURL) });

            const container = document.body.querySelector(containerId);
            this.container = container;
            this._observeContainerResize(container);

            // Registered before any frame can attach, so the frame layers
            // reach every chapter iframe however it arrives, including the
            // ones the pool preloads without announcing.
            this._frameLayerObserver?.disconnect();
            this._frameLayerObserver = new MutationObserver(() => {
                this._ensureFrameLayers();
            });
            this._frameLayerObserver.observe(container, { childList: true, subtree: true });
            const manifestLink = new Link({ href: 'manifest.json' });

            // Create the decrypting fetcher
            const fetcher = new DecryptingHttpFetcher(publicationURL);

            const fetched = fetcher.get(manifestLink);
            const selfLink = (await fetched.link()).toURL(publicationURL);

            let currentPhase = 'manifest_fetch';
            try {
                sendBreadcrumb('init', 'manifest_fetch_start');
                const response = await fetched.readAsJSON();

                currentPhase = 'manifest_parse';
                const manifest = Manifest.deserialize(withRenditionSpread(response));
                manifest.setSelfLink(selfLink);
                this.publication = new Publication({ manifest, fetcher });
                // Reported before any frame exists: the rule can flip the scroll layout
                // off, which reshapes the reader's DOM, and a moved iframe reloads blank.
                this.onSpreadRuleKnown(this.rulesOutSpreads());
                // Fetched here so the OPF reads below overlap with it.
                const positionsTask = this.publication.positionsFromManifest().catch(error => {
                    sendBreadcrumb('init', 'positions_fetch_failed', { errorMessage: error?.message }, 'warning');
                    return [];
                });
                sendBreadcrumb('init', 'manifest_parsed', {
                    readingOrderCount: manifest.readingOrder?.items?.length ?? 0,
                    hasToc: !!(manifest.tableOfContents && manifest.tableOfContents.items?.length),
                });

                currentPhase = 'idref_map';
                // @R2 Build idref→href lookup for matching R1 chapters in R2 reader
                this.idrefToHrefMap = await this.buildIdrefToHrefMap();
                this.highlightManager.idrefToHrefMap = this.idrefToHrefMap;
                sendBreadcrumb('init', 'idref_map_built', { size: this.idrefToHrefMap?.size ?? 0 });

                currentPhase = 'last_position';
                const positions = await positionsTask;
                const { locator: savedPosition, source: lastPositionSource } =
                    await this._resolveInitialPosition(positions);
                // The host and the legacy parser both hand back plain JSON.
                const lastPosition = toInitialLocator(savedPosition);

                sendBreadcrumb('init', 'last_position_resolved', {
                    source: lastPositionSource,
                    chapterHref: lastPosition?.href,
                    progression: lastPosition?.locations?.progression,
                });

                // R2 "defaults" control the reading-system layout constraints
                // (as opposed to "preferences" which are user-adjustable settings).
                // - optimalLineLength: sets --RS__defaultLineLength → body max-width
                //   inside iframes. R2 also uses this value to calculate the inline
                //   width it applies to the #readiumViewport container.
                // - minimalLineLength / maximalLineLength: lower and upper bounds
                //   that R2 enforces when computing viewport and column widths.
                // These values are in characters; R2 converts them to pixels by
                // measuring the "0" digit at the base font size.
                // They are fallbacks only: parseSettings() always submits
                // gutter-compensated bounds as preferences (see getLineLengths),
                // which take precedence over these defaults.

                // Generate accessible fonts CSS with absolute URLs for iframe context
                const accessibleFontsCss = getAccessibleFontsCss(window.location.origin);

                // Null for a fixed-layout publication, which has no column flow.
                const pagedFlowCss = getPagedFlowCss(this._readiumLayout());

                // The spread gutter depends on the reader width, which the
                // constructor did not have yet.
                this.settings = {
                    ...this.settings,
                    ...this.getZoomDependentPreferences(),
                    ...(this.rulesOutSpreads() && { columnCount: 1, scroll: false }),
                };

                currentPhase = 'navigator_construct';
                const nav = this._createNavigator(
                    container,
                    lastPosition,
                    {
                        preferences: this.settings,
                        defaults: {
                            minimalLineLength: LINE_LENGTH_MINIMAL,
                            optimalLineLength: LINE_LENGTH_OPTIMAL,
                            maximalLineLength: LINE_LENGTH_MAXIMAL,
                        },
                        injectables: {
                            rules: [
                                {
                                    resources: [/.*/],
                                    append: [
                                        {
                                            as: 'link',
                                            rel: 'stylesheet',
                                            blob: new Blob([accessibleFontsCss], { type: 'text/css' }),
                                        },
                                        // Pins browser text autosizing off in every frame
                                        // (rotation inflated the text on phones).
                                        {
                                            as: 'link',
                                            rel: 'stylesheet',
                                            blob: new Blob([FRAME_TEXT_SIZE_CSS], { type: 'text/css' }),
                                        },
                                        // Lets a paginated chapter fragment past its
                                        // first column when the publisher pinned the
                                        // body height. Scoped in CSS rather than here
                                        // so it follows a runtime layout change.
                                        ...(pagedFlowCss
                                            ? [
                                                  {
                                                      as: 'link',
                                                      rel: 'stylesheet',
                                                      blob: new Blob([pagedFlowCss], { type: 'text/css' }),
                                                  },
                                              ]
                                            : []),
                                        // Dev-only pagebreak overlay (pagebreaks=1 knob).
                                        // Injected here so every frame gets it at blob
                                        // construction time, including prefetched frames
                                        // that never fire frameLoaded.
                                        ...(this._debugPagebreaks
                                            ? [
                                                  {
                                                      as: 'link',
                                                      rel: 'stylesheet',
                                                      blob: new Blob([PAGEBREAK_DEBUG_CSS], { type: 'text/css' }),
                                                  },
                                              ]
                                            : []),
                                    ],
                                },
                            ],
                            allowedDomains: getFrameAllowedDomains(publicationURL, window.location.origin),
                        },
                    },
                    positions
                );

                this.epubNavigator = nav;
                this._setupFootnoteNavigationGuard(nav);
                this._setupNavigationTargetGuard(nav);
                sendBreadcrumb('init', 'navigator_constructed', { layout: nav.layout });
                this._observeOrientation();

                currentPhase = 'css_prefetch';
                sendBreadcrumb('init', 'lazy_css_cache_init');

                // Lazy CSS pipeline: each <link rel="stylesheet"> is fetched
                // and decrypted on first reference inside DecryptingResource.
                // The cache dedupes concurrent requests via Promise-keyed
                // entries.
                this.lazyCssCache = new LazyCssCache(
                    fetcher,
                    this.epubNavigator.publication.resources?.items ?? [],
                    () => this.idpfFontBlobUrls ?? null
                );
                fetcher.cssCache = this.lazyCssCache;

                // IDPF font deobfuscation must complete BEFORE nav.load() so
                // the LazyCssCache sees a populated font map on its first
                // miss. The cache stores the *processed* CSS string per href
                // and never re-runs font URL rewriting; if the map is still
                // null at first miss, that entry is permanently poisoned and
                // every subsequent frame requesting the same CSS reuses the
                // unrewritten copy.
                //
                // Books without obfuscated fonts (the common case) resolve
                // the task in one round-trip — META-INF/encryption.xml
                // returns 404 and `setupIdpfFontDeobfuscation` short-circuits
                // to an empty Map, so the latency cost is negligible. Books
                // with obfuscated fonts pay the deobfuscation cost upfront,
                // which is the correctness/perf trade-off we accept.
                //
                // If a prior init's IDPF task is still in-flight (re-init
                // before destroy or before the previous task settled), chain
                // a revoke on its eventual map so those blob URLs do not leak.
                if (this._idpfFontsTask) {
                    this._idpfFontsTask
                        .then(map => {
                            for (const url of map.values()) URL.revokeObjectURL(url);
                        })
                        .catch(() => {
                            /* logged at original init-time */
                        });
                }

                this.idpfFontBlobUrls = null;
                this._idpfFontsTask = setupIdpfFontDeobfuscation(fetcher, manifest.metadata?.identifier ?? null)
                    .then(map => {
                        this.idpfFontBlobUrls = map;
                        fetcher.idpfFontBlobUrls = map;
                        sendBreadcrumb('init', 'idpf_fonts_ready', { fontCount: map.size });
                        return map;
                    })
                    .catch(e => {
                        sendBreadcrumb('init', 'idpf_fonts_failed', { errorMessage: e?.message }, 'warning');
                        return new Map();
                    });

                // Block until IDPF deobfuscation settles so the lazy CSS
                // cache can rewrite font url(...) references on its very
                // first miss. See the comment above for the rationale.
                currentPhase = 'idpf_fonts_await';
                await this._idpfFontsTask;

                // Hidden while load() lays the pool out and lands on the saved page.
                const isFixedLayout = nav.layout === 'fixed';
                if (isFixedLayout) {
                    container.style.visibility = 'hidden';
                }

                currentPhase = 'nav_load';
                sendBreadcrumb('init', 'nav_load_start', { layout: nav.layout, isFixedLayout });
                await nav.load();
                sendBreadcrumb('init', 'nav_load_done', { layout: nav.layout, framePoolReady: !!nav.framePool });
                // The navigator has paginated: two requested columns may have
                // rendered as one, which takes the spread gutter off again.
                const gutterCorrection = this._spreadGutterCorrection();
                if (gutterCorrection) await nav.submitPreferences(gutterCorrection);

                if (isFixedLayout) {
                    container.style.visibility = '';
                    sendBreadcrumb('init', 'fxl_layout_applied', { columnCount: this.settings.columnCount });
                }

                this._setupAnimationReplacement(nav);
                this._setupFxlLoadingState(nav);

                // Safety net for frames that attached before the container
                // observer registered (see the registration next to
                // _observeContainerResize).
                this._ensureFrameLayers();

                sendBreadcrumb('init', 'init_complete', {
                    layout: nav.layout,
                    chapterHref: lastPosition?.href,
                });
                resolve(nav);
            } catch (error) {
                sendBreadcrumb(
                    'init',
                    'init_failed',
                    {
                        phase: currentPhase,
                        errorName: error?.name,
                        errorMessage: error?.message,
                    },
                    'error'
                );
                console.error('Error loading manifest', error);
                reject(error);
            }
        });
    }

    /**
     * Constructs the navigator with its mutations already queued (see
     * _serializeNavigatorMutations), so no caller reaches the raw methods.
     * @param {Locator[]} positions - the list load() would otherwise fetch again
     * @returns {EpubNavigator}
     */
    _createNavigator(container, lastPosition, configuration, positions) {
        const nav = new EpubNavigator(
            container,
            this.publication,
            this.listeners,
            positions,
            lastPosition,
            configuration
        );
        this._serializeNavigatorMutations(nav);
        return nav;
    }

    /**
     * Picks the position the navigator opens on: the host's locator, the legacy
     * chapterHref, or the first entry of the position list.
     *
     * A saved position the publication no longer covers is turned down here
     * rather than handed to the navigator: its load() throws on a locator
     * outside the position list or the reading order, which leaves the reader
     * on its spinner. Opening at the first position and go()-ing to the saved
     * one afterwards would avoid the check, at the cost of a second frame load
     * and a first-chapter flash on every open.
     * @param {Locator[]} positions - the publication's position list, possibly empty
     * @returns {Promise<{ locator: Object|Locator|undefined, source: 'r2'|'r1_legacy'|'default_first' }>}
     */
    async _resolveInitialPosition(positions) {
        const coverage = { readingOrderHrefs: this.getReadingOrderHrefs(), positions };

        const fromHost = this._acceptSavedPosition('r2', this.lastPosition?.locator, coverage);
        if (fromHost) return { locator: fromHost, source: 'r2' };

        if (this.lastPosition?.chapterHref) {
            const legacy = await this.parseLastPosition(this.lastPosition);
            const fromLegacy = this._acceptSavedPosition('r1_legacy', legacy, coverage);
            if (fromLegacy) return { locator: fromLegacy, source: 'r1_legacy' };
        }

        // Handed no position, the navigator's load() opens on the first entry of
        // the list it holds, fetching one first when this list is empty.
        return { locator: positions[0], source: 'default_first' };
    }

    /**
     * The saved position as the navigator can open it, or null when the
     * publication does not cover it. A repair or a refusal leaves a breadcrumb.
     * @returns {Object|null}
     */
    _acceptSavedPosition(source, saved, coverage) {
        if (!saved) return null;
        const { locator, reason } = resolveInitialLocator(saved, coverage);
        if (reason) {
            const message = locator ? 'last_position_repaired' : 'last_position_rejected';
            sendBreadcrumb('init', message, { source, reason, chapterHref: saved.href }, 'warning');
        }
        return locator;
    }

    /**
     * Re-points the navigator's event listener at each frame after a preference
     * submit. Without this the reader stops answering selection,
     * scroll and tap. Each frame passes itself as the source, as R2's own
     * wiring does, so the navigator and the footnote guard know which
     * document a report came from.
     */
    _reattachFrameComms(nav) {
        const frameCount = attachedFrames(nav).filter(frame =>
            listenToFrame(frame, (key, value) => nav.eventListener(key, value, frame))
        ).length;
        this._observeReadingInput();
        sendBreadcrumb('frame', 'comms_listeners_reattached', { frameCount });
    }

    /**
     * Runs `mutate` once every earlier navigator mutation has finished. A
     * failed mutation never blocks the next one.
     * @param {() => Promise<void>} mutate
     * @returns {Promise<void>} settles as `mutate` does
     */
    _queueNavigatorMutation(mutate) {
        const turn = this._navigatorMutations.then(() => {
            if (this._anchorKeeper.isDisposed) return;
            return mutate();
        });
        this._navigatorMutations = turn.catch(() => {});
        return turn;
    }

    /**
     * Runs the navigator's preference submits and resizes one at a time, and
     * merges the submits of one tick into one.
     *
     * A submit halts every frame's comms, and a layout flip rebuilds the frame
     * pool, whose new frame completes its show() handshake over those comms.
     * A second submit landing meanwhile aborts the handshake and the frame
     * stays hidden. One queue makes the overlap impossible.
     *
     * After a submit that moves text, the navigator re-counts the columns for
     * the new metrics (Readium does that only in its resize handler), the
     * spread gutter follows the rendered count, and the reader returns to the
     * line they were on (see captureReadingAnchor).
     * @param {EpubNavigator} nav - the navigator, right after construction
     */
    _serializeNavigatorMutations(nav) {
        this._reportMissingNavigatorSurfaces(nav);
        if (!nav?.submitPreferences) return;
        const submitPreferences = nav.submitPreferences.bind(nav);
        const resize = nav.resizeHandler?.bind(nav);
        // load() completes the first frame's show() handshake over the same
        // comms. A submit landing meanwhile halts them and drops the pending
        // acknowledgement, so the book never opens.
        let loading = false;
        if (typeof nav.load === 'function') {
            const load = nav.load.bind(nav);
            nav.load = () =>
                this._queueNavigatorMutation(async () => {
                    loading = true;
                    try {
                        await load();
                    } finally {
                        loading = false;
                    }
                });
        }
        let restoringAnchor = false;
        let dispatchingNavigation = false;
        for (const method of ['go', 'goForward', 'goBackward', 'goLink']) {
            if (typeof nav[method] !== 'function') continue;
            const navigate = nav[method].bind(nav);
            nav[method] = (...args) => {
                if (restoringAnchor || dispatchingNavigation) return navigate(...args);
                this._anchorKeeper.noteUserNavigation();
                const invoke = () => {
                    dispatchingNavigation = true;
                    try {
                        return navigate(...args);
                    } finally {
                        dispatchingNavigation = false;
                    }
                };
                if (!this._anchorKeeper.isFrozen) return invoke();
                const callback = typeof args[args.length - 1] === 'function' ? args.pop() : () => {};
                return this._queueNavigatorMutation(async () => {
                    const result = await withTimeout(
                        new Promise(resolve => {
                            args.push(resolve);
                            invoke();
                        }),
                        READING_ANCHOR_TIMEOUT_MS,
                        false
                    );
                    callback(result);
                });
            };
        }

        const apply = async preferences => {
            await submitPreferences(preferences);
            this._reattachFrameComms(nav);
        };
        const settleLayout = async () => {
            await this._settleFrameFonts();
            await resize?.();
            const correction = this._spreadGutterCorrection();
            if (correction) await apply(correction);
        };
        const settleOnAnchor = async (held, work) => {
            this.beginTrackingSuppression('silent');
            try {
                await work();
                await nextPaint();
                if (!held?.anchor || !this._anchorKeeper.canRestore(held)) return;
                restoringAnchor = true;
                let landing;
                try {
                    landing = this.restoreReadingAnchor(held.anchor);
                } finally {
                    restoringAnchor = false;
                }
                await landing;
            } finally {
                await this.endTrackingSuppression();
                this._anchorKeeper.release();
            }
        };

        let pendingSubmit = null;
        nav.submitPreferences = preferences => {
            if (pendingSubmit) {
                Object.assign(pendingSubmit.preferences, preferences);
                return pendingSubmit.turn;
            }
            const batch = { preferences: { ...preferences } };
            batch.turn = this._queueNavigatorMutation(async () => {
                pendingSubmit = null;
                const movesText = Object.keys(batch.preferences).some(key => !POSITION_NEUTRAL_PREFERENCES.has(key));
                // Fixed layouts keep their pages through any preference: nothing to re-count or return to.
                if (!movesText || this.isFixedLayout()) {
                    await apply(batch.preferences);
                    return;
                }
                await settleOnAnchor(this._anchorKeeper.freeze(), async () => {
                    await apply(batch.preferences);
                    await settleLayout();
                });
            });
            pendingSubmit = batch;
            return batch.turn;
        };

        if (!resize) return;
        // The navigator's ResizeObserver can fire before load() has created
        // the frame pool, and sidebar animations pause resizes until they
        // settle. A burst of callbacks collapses into one resize, which lands
        // on the anchor held from before the geometry changed.
        let pendingResize = null;
        nav.resizeHandler = () => {
            // load() sizes the pool through resizeHandler() before filling it,
            // so that call must not wait on the turn that made it. It is also
            // the one moment between the pool's construction and its landing
            // on the saved page, so the column count goes in here.
            if (loading) {
                this._applyFixedLayoutColumns(nav);
                return resize();
            }
            if (!nav.framePool) {
                sendBreadcrumb('frame', 'resize_skipped_no_framepool', {}, 'warning');
                return Promise.resolve();
            }
            if (this._resizePaused) return Promise.resolve();
            // A position reported since the last landing is the text mid-shift.
            this._anchorKeeper.cancelCapture();
            if (pendingResize) return pendingResize;
            pendingResize = this._queueNavigatorMutation(async () => {
                pendingResize = null;
                const fixedLayout = this.isFixedLayout();
                const held = this._anchorKeeper.freeze({ resize: true });
                if (fixedLayout) held.anchor = this._fixedLayoutResizeTarget();
                this._suppressAnimation = fixedLayout;
                try {
                    await settleOnAnchor(held, fixedLayout ? resize : settleLayout);
                } finally {
                    this._suppressAnimation = false;
                }
            });
            return pendingResize;
        };
    }

    /**
     * Reports the navigator members this viewer patches or reads that are no
     * longer there. Each lives on R2's prototype from construction, so a miss
     * means an upgrade renamed or dropped it. Nothing throws when that happens:
     * the queue is never installed and the frames read as empty, so the reader
     * drifts through reflows again with no error to follow.
     * @param {EpubNavigator} nav - the navigator, right after construction
     */
    _reportMissingNavigatorSurfaces(nav) {
        const missing = NAVIGATOR_SURFACES.filter(name => !nav || !(name in nav));
        if (!missing.length) return;
        Sentry.captureMessage('readium.navigator_surface_missing', { level: 'warning', extra: { missing } });
    }

    /**
     * Readium's pool starts in spread mode whatever the reader asked for and
     * only learns the column count from a later preference. Landing on the
     * saved page in a spread aligns an odd page onto the next one, and going
     * single afterwards keeps that slide: the book opens one page past where
     * it was closed.
     * @param {EpubNavigator} nav - the navigator, once load() has built its pool
     */
    _applyFixedLayoutColumns(nav) {
        if (nav.layout !== 'fixed' || !nav.framePool) return;
        nav.framePool.setPerPage(this.settings.columnCount);
    }

    /**
     * Replace CSS transition animation with hide/show for Fixed Layout EPUBs.
     *
     * Readium's updateSpineStyle method uses CSS transitions (150-500ms) when changing
     * layout or resizing, causing a visible "sliding" animation. This patch replaces
     * that animation with a brief hide/show to prevent the visual distraction.
     *
     * @param {Object} nav - The EpubNavigator instance
     */
    _setupAnimationReplacement(nav) {
        // Only apply to Fixed Layout EPUBs
        if (!this.isFixedLayout() || !nav.framePool) return;

        const pool = nav.framePool;

        // Check if spineElement exists (FXLFramePoolManager)
        if (!pool.spineElement) return;

        const originalUpdateSpineStyle = pool.updateSpineStyle.bind(pool);
        const viewer = this; // Reference to EpubViewer instance for flag access

        pool.updateSpineStyle = function (animate, fast = true) {
            // Only suppress animation when flag is set (layout change or sidebar reflow)
            if (animate && viewer._suppressAnimation) {
                // Apply changes without animation
                originalUpdateSpineStyle(false, fast);
            } else {
                // Normal behavior: use original animation or no animation as requested
                originalUpdateSpineStyle(animate, fast);
            }
        };
    }

    /**
     * Wraps FXLFramePoolManager.update() to manage loading state during navigation.
     *
     * PATCH: In Readium 2 FXL, there is no loading indicator during page
     * navigation. The user sees a blank screen while framePool.update()
     * builds blobs and loads iframes.
     *
     * Shows a loading indicator only when the current frame is blank (not
     * pre-loaded). Uses a MutationObserver on the iframe's class attribute
     * to detect the exact moment Readium adds the "loaded" class (when
     * content becomes visible). Falls back to clearing loading when update()
     * resolves as a safety net.
     *
     * @param {Object} nav - The EpubNavigator instance
     */
    _setupFxlLoadingState(nav) {
        if (!this.isFixedLayout() || !nav.framePool) return;

        const pool = nav.framePool;
        const originalUpdate = pool.update.bind(pool);
        const viewer = this;
        let pendingUpdates = 0;
        let loadingActive = false;
        let activeObserver = null;

        const stopObserving = () => {
            if (activeObserver) {
                activeObserver.disconnect();
                activeObserver = null;
            }
        };

        const hideLoading = () => {
            if (loadingActive) {
                loadingActive = false;
                stopObserving();
                viewer.onNavigationEnd();
            }
        };

        this._isFxlLoading = () => loadingActive;

        pool.update = async function (...args) {
            pendingUpdates++;
            stopObserving();

            const updatePromise = originalUpdate(...args);

            const currentFrames = pool.currentFrames.filter(Boolean);
            const isScreenBlank = currentFrames.some(fm => !fm.iframe.classList.contains('loaded'));

            if (isScreenBlank) {
                if (!loadingActive) {
                    loadingActive = true;
                    viewer.onNavigationStart();
                }

                activeObserver = new MutationObserver(() => {
                    const allLoaded = currentFrames.every(fm => fm.iframe.classList.contains('loaded'));
                    if (allLoaded) {
                        hideLoading();
                    }
                });

                currentFrames.forEach(fm => {
                    activeObserver.observe(fm.iframe, {
                        attributes: true,
                        attributeFilter: ['class'],
                    });
                });
            }

            try {
                await updatePromise;
            } finally {
                pendingUpdates--;
                if (pendingUpdates === 0) {
                    stopObserving();
                    hideLoading();
                }
            }
        };

        const originalGoForward = nav.goForward.bind(nav);
        nav.goForward = (animated, cb) => {
            if (loadingActive) return cb(false);
            originalGoForward(animated, cb);
        };

        const originalGoBackward = nav.goBackward.bind(nav);
        nav.goBackward = (animated, cb) => {
            if (loadingActive) return cb(false);
            originalGoBackward(animated, cb);
        };

        const originalGo = nav.go.bind(nav);
        nav.go = (locator, animated, cb) => {
            if (loadingActive) return cb(false);
            originalGo(locator, animated, cb);
        };

        const originalGoLink = nav.goLink.bind(nav);
        nav.goLink = (link, animated, cb) => {
            if (loadingActive) return cb(false);
            originalGoLink(link, animated, cb);
        };
    }

    /**
     * Get the chapter HREF from content.opf file using robust XML parsing
     * Retrieves the OPF file path from META-INF/container.xml.
     * EPUB standard requires container.xml to define the rootfile location.
     *
     * @returns {Promise<{opfPath: string, opfDir: string}|null>} Object with opfPath (full path to OPF)
     *          and opfDir (directory containing OPF for resolving relative hrefs), or null on failure
     */
    async getOpfPathFromContainer() {
        try {
            const containerLink = new Link({ href: 'META-INF/container.xml' });
            const container = await this.publication.fetcher.get(containerLink);
            const containerText = await container.readAsString();

            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(containerText, 'application/xml');

            const parserError = xmlDoc.querySelector('parsererror');
            if (parserError) {
                console.error('XML parsing error in container.xml:', parserError.textContent);
                return null;
            }

            const rootfiles = xmlDoc.getElementsByTagNameNS('*', 'rootfile');
            for (const rootfile of rootfiles) {
                const mediaType = rootfile.getAttribute('media-type');
                if (mediaType === 'application/oebps-package+xml') {
                    const opfPath = rootfile.getAttribute('full-path');
                    if (opfPath) {
                        const lastSlash = opfPath.lastIndexOf('/');
                        const opfDir = lastSlash >= 0 ? opfPath.substring(0, lastSlash + 1) : '';
                        return { opfPath, opfDir };
                    }
                }
            }

            console.warn('No OPF rootfile found in container.xml');
            return null;
        } catch (error) {
            console.error('Error reading container.xml:', error);
            return null;
        }
    }

    /**
     * Get the chapter HREF from OPF file using robust XML parsing.
     * Reads META-INF/container.xml to find the OPF location.
     */
    async getContentOpf(legacyLastPosition) {
        if (!legacyLastPosition || !legacyLastPosition.chapterHref) {
            return null;
        }

        try {
            const opfInfo = await this.getOpfPathFromContainer();
            if (!opfInfo) {
                console.warn('Could not determine OPF path from container.xml');
                return null;
            }

            const { opfPath, opfDir } = opfInfo;
            const contentOpfLink = new Link({ href: opfPath });
            const contentOpf = await this.publication.fetcher.get(contentOpfLink);
            const contentOpfText = await contentOpf.readAsString();

            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(contentOpfText, 'application/xml');

            const parserError = xmlDoc.querySelector('parsererror');
            if (parserError) {
                console.error('XML parsing error in OPF:', parserError.textContent);
                return null;
            }

            const target = legacyLastPosition.chapterHref;

            let manifestItem = null;
            const items = xmlDoc.getElementsByTagNameNS('*', 'item');
            for (const item of items) {
                const itemId = item.getAttribute('id');
                const itemHref = item.getAttribute('href');
                if (itemId === target || itemHref === target) {
                    manifestItem = item;
                    break;
                }
            }

            if (!manifestItem) {
                const normalizedTarget = target.replace(/^(\.\.\/)+([^/]+\/)?/, '').replace(/^[^/]+\//, '');
                for (const item of items) {
                    const itemHref = item.getAttribute('href') || '';
                    if (itemHref.endsWith(normalizedTarget) || normalizedTarget.endsWith(itemHref.split('/').pop())) {
                        manifestItem = item;
                        break;
                    }
                }
            }

            if (!manifestItem) {
                console.warn(`Chapter "${target}" not found in OPF manifest`);
                return null;
            }

            const href = manifestItem.getAttribute('href');
            if (!href) {
                console.warn(`No href attribute found for chapter "${target}"`);
                return null;
            }

            const normalizedHref = href.startsWith(opfDir) ? href : `${opfDir}${href}`;

            return normalizedHref;
        } catch (error) {
            console.error('Error parsing OPF file:', error);
            return null;
        }
    }

    /**
     * @R2 Build a lookup map from OPF manifest item id (idref) to href.
     *
     * R1 stored epubChapter as the spine item's idref (e.g. "book2"),
     * while R2 uses the actual file href (e.g. "OEBPS/GeographyofBli_body_split_002.html").
     * This map bridges the gap so legacy highlights can be matched to the
     * correct chapter in the R2 reader.
     *
     * @returns {Map<string, string>} idref → href (raw href from OPF manifest)
     */
    async buildIdrefToHrefMap() {
        const map = new Map();

        try {
            const opfInfo = await this.getOpfPathFromContainer();
            if (!opfInfo) {
                console.warn('@R2 buildIdrefToHrefMap: could not determine OPF path from container.xml');
                return map;
            }

            const { opfPath, opfDir } = opfInfo;
            const contentOpfLink = new Link({ href: opfPath });
            const contentOpf = await this.publication.fetcher.get(contentOpfLink);
            const contentOpfText = await contentOpf.readAsString();

            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(contentOpfText, 'application/xml');

            if (xmlDoc.querySelector('parsererror')) {
                console.warn('@R2 buildIdrefToHrefMap: OPF parse error');
                return map;
            }

            const items = xmlDoc.getElementsByTagNameNS('*', 'item');
            for (const item of items) {
                const itemId = item.getAttribute('id');
                const itemHref = item.getAttribute('href');
                if (itemId && itemHref) {
                    const fullHref = itemHref.startsWith(opfDir) ? itemHref : `${opfDir}${itemHref}`;
                    map.set(itemId, fullHref);
                }
            }

            console.log(`@R2 buildIdrefToHrefMap: built map with ${map.size} entries`);
        } catch (error) {
            console.warn('@R2 buildIdrefToHrefMap: could not fetch OPF:', error.message);
        }

        return map;
    }

    async parseLastPosition(legacyLastPosition) {
        // First try to resolve the legacy chapterHref directly from the
        // readingOrder — this avoids fetching content.opf which the Go
        // streamer does not serve as a standalone resource.
        let chapterHref = this._resolveChapterFromReadingOrder(legacyLastPosition);

        // Fallback: try content.opf if readingOrder lookup failed
        if (!chapterHref) {
            chapterHref = await this.getContentOpf(legacyLastPosition);
        }

        if (!chapterHref) return undefined;

        let progression = 0;
        if (legacyLastPosition.cfi) {
            const lastReadingPositionCFI = parseCFI(legacyLastPosition.cfi);
            progression = parseInt(lastReadingPositionCFI.elementPath[1]) / 100 || 0;
        }

        // No position number: the initial-position check fills it in from the
        // list, which is what the frame pool goes by rather than the href.
        return {
            href: chapterHref,
            locations: { progression },
            type: 'application/xhtml+xml',
            title: '',
        };
    }

    /**
     * Resolve a legacy R1 chapterHref directly from the publication's
     * readingOrder without needing to fetch content.opf.
     *
     * The legacy chapterHref can be:
     * - A full OEBPS-relative path: "OEBPS/page004.xhtml"
     * - A bare filename: "page004.xhtml"
     * - An OPF item id: "book4" (cannot be resolved here)
     *
     * The three passes below run exact → longest-tail → filename-only, each one
     * less specific than the last, and the order is load-bearing rather than
     * incidental: a book that repeats a filename across directories (a
     * "page004.xhtml" in both front matter and body text) resolves a legacy
     * "text/page004.xhtml" to the body copy only because the tail pass runs
     * first — the filename pass would return whichever copy the readingOrder
     * lists earlier. Keep the passes in this order, and keep this rule here
     * rather than folding it into the filename-only normalisation used for
     * table-of-contents titles. Covered by
     * EpubViewer.resolveChapterFromReadingOrder.test.js.
     */
    _resolveChapterFromReadingOrder(legacyLastPosition) {
        if (!legacyLastPosition?.chapterHref || !this.publication?.readingOrder?.items) {
            return null;
        }

        const target = legacyLastPosition.chapterHref;
        const items = this.publication.readingOrder.items;

        // Direct match
        const directMatch = items.find(item => item.href === target);
        if (directMatch) return directMatch.href;

        // Suffix match (e.g. "page004.xhtml" matches "OEBPS/page004.xhtml")
        const suffixMatch = items.find(item => item.href.endsWith(`/${target}`) || target.endsWith(`/${item.href}`));
        if (suffixMatch) return suffixMatch.href;

        // Filename-only match
        const targetFilename = target.split('/').pop();
        const filenameMatch = items.find(item => item.href.split('/').pop() === targetFilename);
        if (filenameMatch) return filenameMatch.href;

        return null;
    }

    /**
     * Extract the publication-relative directory from the Readium <base> href.
     * e.g. "https://host/token/OEBPS/cover.xhtml" with pub base "https://host/token/"
     *      → "OEBPS/"
     * Returns null if the base href can't be resolved.
     */
    _dirFromBaseHref(baseHref) {
        if (!baseHref) return null;
        try {
            const pubBase = this.publication?.baseURL;
            if (!pubBase) return null;
            // Strip the publication base to get the relative item path
            let relative = baseHref;
            if (relative.startsWith(pubBase)) {
                relative = relative.substring(pubBase.length);
            }
            // Get directory part
            const lastSlash = relative.lastIndexOf('/');
            return lastSlash >= 0 ? relative.substring(0, lastSlash + 1) : '';
        } catch {
            return null;
        }
    }

    /**
     * Extract the publication-relative href from a full URL.
     * e.g. "https://host/token/OEBPS/toc.xhtml" with pub base "https://host/token/"
     *      → "OEBPS/toc.xhtml"
     * @param {string} fullHref - Full URL from <base data-readium>
     * @returns {string} Relative href suitable for R2 Locator navigation
     */
    _extractRelativeHref(fullHref) {
        if (!fullHref) return '';
        try {
            const pubBase = this.publication?.baseURL;
            if (!pubBase) return fullHref;
            if (fullHref.startsWith(pubBase)) {
                return fullHref.substring(pubBase.length);
            }
            return fullHref;
        } catch {
            return fullHref;
        }
    }

    /**
     * Determines if the current frame is a cover document.
     * Checks multiple indicators: first item in readingOrder, 'cover' rel,
     * or DOM elements with cover-related classes/attributes.
     * @param {Window} _wnd - The iframe window object
     * @returns {boolean} True if the frame appears to be a cover
     */
    _isCoverFrame(_wnd) {
        // A fixed page lays itself out at its own size, with no gutter to clip it.
        if (this.isFixedLayout()) return false;

        try {
            const currentHref = this.epubNavigator?.currentLocation?.href;
            if (!currentHref) return false;

            const readingOrder = this.publication?.readingOrder?.items;
            if (!readingOrder?.length) return false;

            const firstItem = readingOrder[0];

            // Check 1: Is this the first item in readingOrder?
            const isFirstItem =
                currentHref.endsWith(firstItem.href) || firstItem.href.endsWith(currentHref.split('/').pop());

            // Check 2: Does the first item have 'cover' in its rels?
            const hasCoverRel =
                firstItem.rels?.has?.('cover') || (Array.isArray(firstItem.rels) && firstItem.rels.includes('cover'));

            // Check 3: Does the DOM contain cover-related elements?
            const doc = _wnd.document;
            const hasCoverElements = !!(
                doc.querySelector('section.cover') ||
                doc.querySelector('.cover') ||
                doc.querySelector('.capa') ||
                doc.querySelector('[epub\\:type="cover"]') ||
                doc.querySelector('img.coverimage') ||
                doc.querySelector('img[role="doc-cover"]') ||
                doc.querySelector('svg > image') // SVG-wrapped cover images
            );

            // Check 4: Is the body class 'fullpage' with cover section (common EPUB pattern)?
            const hasFullpageCover =
                doc.body?.classList?.contains('fullpage') &&
                (doc.querySelector('section.cover') || doc.querySelector('.capa'));

            return (
                (isFirstItem && (hasCoverRel || hasCoverElements)) ||
                hasFullpageCover ||
                (isFirstItem && hasCoverElements)
            );
        } catch {
            return false;
        }
    }

    /**
     * Builds the preference entries whose pixel values depend on the
     * font-size zoom factor, from the raw values tracked on the instance.
     *
     * Readium2 paints these paddings inside the body's zoomed coordinate
     * space, so they are divided by the zoom factor to keep their painted
     * size equal to the configured value (see getZoomCompensatedPadding).
     * The line-length bounds are derived from the compensated gutter so the
     * margins setting insets the text by the same painted amount at every
     * font size (DEV-4883).
     *
     * Every path that changes one of the inputs (margins, font size, footer
     * clearance) submits this whole group so the values never drift apart.
     *
     * @param {Object} [options]
     * @param {number} [options.fontSize] - Font size factor (e.g. 1, 1.5) the
     *   values are compensated for; defaults to the current font size.
     * @param {boolean} [options.isSpread] - Whether the gutter is for a
     *   rendered double-page spread; defaults to the current layout. A layout
     *   switch passes the state of the layout it targets.
     * @returns {Object} Preference entries for submitPreferences/parseSettings
     */
    getZoomDependentPreferences({ fontSize = this.currentFontSize, isSpread = this._isReflowableSpread() } = {}) {
        const zoom = getEffectiveZoom(fontSize);
        const compensate = paddingPx => getZoomCompensatedPadding(paddingPx, zoom, IS_ZOOM_BASED_FONT_SIZING);
        const gutter = compensate(this.getEffectivePageGutter(isSpread));
        return {
            fontSize: zoom,
            pageGutter: gutter,
            // ReadiumCSS applies `--RS__pageGutter` as body horizontal padding only
            // when scroll is off (selector: `:root:not([style*="readium-scroll-on"]) body`).
            // Mirror the gutter into scrollPaddingLeft/Right so horizontal margins
            // remain effective in scroll mode.
            scrollPaddingLeft: gutter,
            scrollPaddingRight: gutter,
            // Vertical scroll padding clears the fixed header/footer bars from
            // inside the scroll flow (--RS__scrollPaddingTop/Bottom -> body
            // padding, scroll mode only), so content scrolls seamlessly under the
            // bars while the first and last lines stay clear of them.
            scrollPaddingTop: compensate(this.currentScrollPaddingTop),
            scrollPaddingBottom: compensate(this.currentScrollPaddingBottom),
            // Line-length bounds compensated for the gutter, so the margins
            // setting visibly insets the text on viewports wider than the
            // maximal line length (DEV-4883), with the cap of the layout the
            // bounds are for. See getLineLengths.
            ...getLineLengths(gutter, { isSpread }),
        };
    }

    parseSettings(settings) {
        const themeColors = this.getThemePreferences(settings.theme);

        const r2Settings = {
            backgroundColor: themeColors.backgroundColor,
            blendFilter: null,
            columnCount: settings.layout == null || settings.layout === 'single' ? 1 : 2,
            constraint: null,
            darkenFilter: null,
            deprecatedFontSize: null,
            fontFamily: settings.fontFamily || null,
            fontSizeNormalize: null,
            fontOpticalSizing: null,
            fontOverride: null,
            fontWeight: null,
            fontWidth: null,
            hyphens: null,
            invertFilter: null,
            invertGaijiFilter: null,
            iPadOSPatch: null,
            layoutStrategy: null,
            letterSpacing: null,
            ligatures: null,
            linkColor: themeColors.linkColor,
            noRuby: null,
            ...getParagraphPreferences(settings.lineHeight),
            scroll: settings.layout === 'scroll',
            // fontSize, pageGutter, scrollPaddingLeft/Right (gutter mirrored for scroll
            // mode), scrollPaddingTop/Bottom (bar clearances) and the
            // gutter-compensated line-length bounds (DEV-4883), all corrected
            // for the font-size zoom factor.
            ...this.getZoomDependentPreferences(),
            selectionBackgroundColor: themeColors.selectionBackgroundColor,
            selectionTextColor: themeColors.selectionTextColor,
            textAlign: null,
            textColor: themeColors.textColor,
            textNormalization: null,
            visitedColor: null,
            wordSpacing: null,
        };
        return r2Settings;
    }

    preventContextMenu(e) {
        e.preventDefault();
        e.stopPropagation();
    }

    /** One function for every frame, so a repeated install keeps one listener. */
    _onFrameKeydown = e => {
        try {
            this.handleKeyboardArrowKeys(e);
        } catch (error) {
            console.warn('Error handling keyboard event:', error);
        }
    };

    handleKeyboardArrowKeys(e) {
        if (!this.isNavigationEnabled() || !this.isKeyboardNavigationEnabled() || this._isFxlLoading()) {
            return;
        }

        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            e.preventDefault();
            this.onKeyboardArrowPressed(e);
        }

        // Arrow keys name screen sides, and Vue's page actions move Readium by side,
        // so an RTL book needs no swap.
        if (e.key === 'ArrowLeft') {
            this.requestPrevPage();
        }
        if (e.key === 'ArrowRight') {
            this.requestNextPage();
        }
    }

    /**
     * Get table of contents items from the current publication.
     *
     * @readium/shared 2.x: tableOfContents was renamed to toc.
     * R2 ToC items use `title` and `href`, but ContentList.vue expects `label` and `chapterHref`.
     * This method normalizes the items to the expected format.
     */
    getTocItems() {
        const rawItems = this.epubNavigator.publication?.toc?.items || [];
        return this.normalizeTocItems(rawItems);
    }

    getReadingOrderHrefs() {
        return (this.publication?.readingOrder?.items || []).map(item => item.href).filter(Boolean);
    }

    setTableOfContents(items) {
        this._tocIndex = buildEpubTocIndex(items, this.getReadingOrderHrefs());
        this._tocTargetCache = new WeakMap();
        this._lastTocLocation = null;
        this._lastTocResource = '';
    }

    _frameResource(frameWindow) {
        return readFrameResource(frameWindow);
    }

    _frameWindowFor(locatorHref, preferredWindow = null) {
        if (preferredWindow && epubResourcesMatch(this._frameResource(preferredWindow), locatorHref)) {
            return preferredWindow;
        }

        return (
            this._attachedFrameWindows().find(wnd => epubResourcesMatch(this._frameResource(wnd), locatorHref)) ?? null
        );
    }

    _tocTarget(document, fragment) {
        let targets = this._tocTargetCache.get(document);
        if (!targets) {
            targets = new Map();
            this._tocTargetCache.set(document, targets);
        }

        if (targets.has(fragment)) return targets.get(fragment);

        const target = findEpubTocTarget(document, fragment);
        targets.set(fragment, target);

        return target;
    }

    _tocAnchorState(locatorHref, frameWindow) {
        const document = frameWindow?.document;
        const body = document?.body;
        if (!body) return { isReady: false, validAnchorKeys: [], startedAnchorKeys: [] };

        const resourceAnchors = (this._tocIndex?.entries || [])
            .filter(entry => entry.fragment && epubResourcesMatch(entry.resource, locatorHref))
            .map(entry => ({ key: entry.targetKey, fragment: entry.fragment, sourceOrder: entry.sourceOrder }));
        const anchors = locateEpubTocAnchors(document, resourceAnchors, (frameDocument, fragment) =>
            this._tocTarget(frameDocument, fragment)
        );

        const firstVisibleElement = findFirstVisibleElement(frameWindow, body, this._frameEdgeContext());

        return {
            isReady: firstVisibleElement !== body || anchors.some(anchor => anchor.element === body),
            validAnchorKeys: anchors.map(anchor => anchor.key),
            startedAnchorKeys: listStartedEpubTocAnchorKeys(anchors, firstVisibleElement),
        };
    }

    updateCurrentTocLocation(locator = this.getCurrentLocation(), preferredWindow = null) {
        if (!this._tocIndex || !locator?.href) return;

        const resource = normalizeEpubResource(locator.href);
        const frameWindow = this._frameWindowFor(locator.href, preferredWindow);
        if (!frameWindow && resource === this._lastTocResource && this._lastTocLocation?.path) return;

        const anchorState = frameWindow
            ? this._tocAnchorState(locator.href, frameWindow)
            : { isReady: false, validAnchorKeys: [], startedAnchorKeys: [] };
        if (!anchorState.isReady && resource === this._lastTocResource && this._lastTocLocation?.path) return;

        const location = resolveEpubTocLocation({
            index: this._tocIndex,
            locatorHref: locator.href,
            validAnchorKeys: anchorState.validAnchorKeys,
            startedAnchorKeys: anchorState.startedAnchorKeys,
        });

        this._lastTocResource = resource;
        if (epubTocLocationsEqual(location, this._lastTocLocation)) return;

        this._lastTocLocation = location;
        this.onTocLocationChanged(location);
    }

    /**
     * Recursively normalize R2 ToC items to the format expected by ContentList.vue.
     * Maps R2 { title, href, children } → { label, chapterHref, items }
     */
    normalizeTocItems(items) {
        if (!items || !Array.isArray(items)) return [];
        return items.map(item => ({
            label: item.title || item.label || '',
            chapterHref: item.href || item.chapterHref || '',
            page: item.page || null,
            items: this.normalizeTocItems(item.children || item.items || []),
        }));
    }

    getCurrentLocation() {
        return this.epubNavigator.currentLocation;
    }

    /**
     * Map Volpe theme names to R2 color preferences.
     *
     * Readium 2 does NOT have a "theme" preference — it uses individual
     * color properties (backgroundColor, textColor, linkColor) that get
     * applied as CSS custom properties inside the EPUB iframes.
     */
    static themePreferences = {
        dark: {
            backgroundColor: '#141414',
            textColor: '#d7d7db',
            linkColor: '#368ace',
            selectionBackgroundColor: '#4a5a7a',
            selectionTextColor: '#ffffff',
        },
        light: {
            backgroundColor: null,
            textColor: null,
            linkColor: null,
            selectionBackgroundColor: null,
            selectionTextColor: null,
        },
        cream: {
            backgroundColor: '#fefce8',
            textColor: '#38342C',
            linkColor: '#946B2D',
        },
        sepia: {
            backgroundColor: '#f4ecd8',
            textColor: '#5C4033',
            linkColor: '#6B4F0F',
        },
        cyan: {
            backgroundColor: '#ecfeff',
            textColor: '#164E63',
            linkColor: '#0B5E73',
        },
        'high-contrast-light': {
            backgroundColor: '#ffffff',
            textColor: '#000000',
            linkColor: '#0000CC',
        },
        'high-contrast-dark': {
            backgroundColor: '#000000',
            textColor: '#FFFFFF',
            linkColor: '#00E5FF',
        },
    };

    getThemePreferences(theme) {
        if (theme === 'system') {
            const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
            return EpubViewer.themePreferences[isDark ? 'dark' : 'light'];
        }
        return EpubViewer.themePreferences[theme] || EpubViewer.themePreferences.light;
    }

    changeTheme(theme) {
        sendBreadcrumb('layout', 'theme_changed', { theme });
        this.epubNavigator.submitPreferences(this.getThemePreferences(theme));
    }

    /**
     * Changes the font family for EPUB content.
     * @param {string|null} fontFamily - CSS font-family value or null for default
     */
    changeFontFamily(fontFamily) {
        sendBreadcrumb('layout', 'font_family_changed', { fontFamily });
        this.epubNavigator?.submitPreferences({ fontFamily });
    }

    /**
     * Changes the line height for EPUB content.
     * @param {number|null} lineHeight - Line height value (e.g. 1.2, 1.5, 2.0) or null for default
     */
    changeLineHeight(lineHeight) {
        const preferences = getParagraphPreferences(lineHeight);
        sendBreadcrumb('layout', 'line_height_changed', preferences);
        this.epubNavigator?.submitPreferences(preferences);
    }

    /**
     * Changes the page margins for EPUB content.
     * @param {number} pageGutter - Margin value in pixels (e.g. 5, 20, 60)
     */
    changeMargins(pageGutter) {
        this.currentMargins = pageGutter;
        const preferences = this.getZoomDependentPreferences();
        sendBreadcrumb('layout', 'margins_changed', { pageGutter, ...preferences });
        this.epubNavigator?.submitPreferences(preferences);
    }

    /**
     * Gutter applied to each column side: the margins setting plus, in a
     * rendered reflowable double-page spread, the width-proportional
     * allowance that keeps the two pages apart (see getSpreadGutter).
     * @param {boolean} [isSpread] - Defaults to the current layout; a layout
     *   switch passes the state of the layout it targets.
     * @returns {number} Gutter in px, before font-size zoom compensation.
     */
    getEffectivePageGutter(isSpread = this._isReflowableSpread()) {
        if (!isSpread) return this.currentMargins;
        // The navigator sizes its own container to the spread, so the reader
        // width is the parent's; the window stands in before mount.
        const readerWidth = this.container?.parentElement?.clientWidth || window.innerWidth;
        return getSpreadGutter(this.currentMargins, readerWidth);
    }

    /**
     * The zoom-dependent preferences to re-submit when the rendered layout no
     * longer matches the gutter the navigator applies: the reader width moved
     * or two requested columns rendered as one (or two again). null when the
     * gutter is unchanged, so a resize never loops.
     * @returns {Object|null}
     */
    _spreadGutterCorrection() {
        if (!this.epubNavigator) return null;
        const preferences = this.getZoomDependentPreferences();
        if (preferences.pageGutter === this.epubNavigator.settings?.pageGutter) return null;
        sendBreadcrumb('layout', 'spread_gutter_changed', preferences);
        return preferences;
    }

    /**
     * Sets the scroll-mode clearances so the first and last lines clear the
     * fixed header and footer, through Readium's native scrollPaddingTop and
     * scrollPaddingBottom preferences.
     * @param {{ top: number, bottom: number }} clearances - Clearances in px.
     */
    changeScrollClearances({ top, bottom }) {
        if (top === this.currentScrollPaddingTop && bottom === this.currentScrollPaddingBottom) return;
        this.currentScrollPaddingTop = top;
        this.currentScrollPaddingBottom = bottom;
        // Both go out: the navigator drops an unchanged value, so the
        // untouched side costs nothing.
        const { scrollPaddingTop, scrollPaddingBottom } = this.getZoomDependentPreferences();
        this.epubNavigator?.submitPreferences({ scrollPaddingTop, scrollPaddingBottom });
    }

    updateLayout(layout = 'single', onComplete = () => {}) {
        if (this.rulesOutSpreads()) return;

        const preferences = {};

        if (layout === 'scroll' && this.isPreviewMode) {
            console.debug('Prevent scroll mode in EPUB previews');
            layout = 'single';
        }

        // Resolve the layout config AFTER the preview downgrade: the branch below
        // keys off `epubLayout.scroll`, so capturing it before the downgrade would
        // still submit scroll for a preview (e.g. the debug menu's live layout knob).
        const epubLayout = this.epubLayouts[layout];

        if (epubLayout.scroll === 'scroll-doc') {
            preferences.scroll = true;
        } else {
            preferences.scroll = false;

            if (layout === 'single') {
                preferences.columnCount = 1;
            } else if (layout === 'double') {
                preferences.columnCount = 2;
            } else {
                preferences.columnCount = null;
            }
        }

        sendBreadcrumb('layout', 'layout_changed', {
            layout,
            columnCount: preferences.columnCount ?? null,
        });

        // Suppress animation during layout change
        this._suppressAnimation = true;

        if (this.isFixedLayout()) {
            this._applyLayoutWithFade(preferences, epubLayout, onComplete);
        } else {
            // The switch carries the gutter of the layout it targets in the
            // same submit; the queue takes the spread allowance off again if
            // two requested columns rendered as one.
            const isSpread = !preferences.scroll && preferences.columnCount === 2;
            const layoutPreferences = { ...preferences, ...this.getZoomDependentPreferences({ isSpread }) };

            // onComplete() flips the Vuex scroll state, whose watcher submits the
            // scroll clearance. That belongs after the layout it clears for.
            Promise.resolve(this.epubNavigator.submitPreferences(layoutPreferences))
                .catch(error => {
                    sendBreadcrumb(
                        'layout',
                        'layout_submit_failed',
                        {
                            error: error?.message,
                        },
                        'warning'
                    );
                })
                .finally(() => {
                    this._suppressAnimation = false;
                    onComplete(epubLayout);
                });
        }
    }

    /**
     * Applies layout change with fade animation for Fixed Layout EPUBs.
     * Uses ReadiumFadeAnimator for the common fade pattern.
     * @param {Object} preferences - Readium preferences to apply
     * @param {Object} epubLayout - Layout configuration object
     * @param {Function} onComplete - Callback when layout change is complete
     */
    _applyLayoutWithFade(preferences, epubLayout, onComplete) {
        sendBreadcrumb('layout', 'layout_fade_start', { epubLayout: epubLayout?.scroll ?? null });
        const bookElement = this.getBookElement();

        if (!bookElement) {
            this.epubNavigator.submitPreferences(preferences);
            setTimeout(() => {
                this._suppressAnimation = false;
            }, 200);
            onComplete(epubLayout);
            sendBreadcrumb('layout', 'layout_fade_end', { epubLayout: epubLayout?.scroll ?? null });
            return;
        }

        this._fadeAnimator.animate(
            bookElement,
            async () => {
                await this.epubNavigator.submitPreferences(preferences);
            },
            () => {
                this._suppressAnimation = false;
                onComplete(epubLayout);
                sendBreadcrumb('layout', 'layout_fade_end', { epubLayout: epubLayout?.scroll ?? null });
            }
        );
    }

    /**
     * Submits the current font size together with the zoom-dependent pixel
     * paddings, which must be recompensated for the new zoom factor (see
     * getZoomDependentPreferences).
     */
    _submitFontSizePreferences() {
        return this.epubNavigator?.submitPreferences(this.getZoomDependentPreferences());
    }

    /**
     * Pauses the ResizeObserver handling during sidebar animations.
     * Used for Fixed Layout EPUBs to prevent intermediate reflows.
     */
    pauseResizeHandling() {
        this._resizePaused = true;
    }

    /**
     * Resumes the ResizeObserver handling after sidebar animations complete.
     */
    resumeResizeHandling() {
        this._resizePaused = false;
    }

    /**
     * Gets the book element for Fixed Layout EPUBs.
     * Unlike spineElement, bookElement is not modified by Readium during layout changes,
     * making it safe for fade animations.
     * @returns {HTMLElement|null} The book element or null if not available
     */
    getBookElement() {
        if (this.epubNavigator?.layout !== 'fixed') return null;
        return this.epubNavigator?.pool?.bookElement || null;
    }

    /**
     * Checks if the current EPUB is Fixed Layout.
     * @returns {boolean}
     */
    isFixedLayout() {
        return this._readiumLayout() === 'fixed';
    }

    /**
     * Checks if the book is fixed-layout and marked `rendition:spread none`, which
     * Readium shows one page at a time in every layout.
     * @returns {boolean}
     */
    rulesOutSpreads() {
        return this.isFixedLayout() && this.publication?.metadata.otherMetadata?.spread === 'none';
    }

    /**
     * Reports the printed pages of the current fixed-layout spread. Each FXL
     * spine file is one page carrying at most one pagebreak marker (injected
     * right after <body>, or the publisher's own), so the current pages are
     * read straight from the spread's frames.
     *
     * A present marker is authoritative even when unusable: label 0 (the
     * cover) and non-numeric labels mean "this sheet has no addressable
     * page", not "guess from position" — publisher numbering can be offset
     * from spine order. Only a file with no marker at all falls back to its
     * reading position (one per spine file in FXL).
     *
     * A spread with no addressable page (e.g. the cover) reports nothing,
     * keeping the last known state — same policy as the tracker's
     * empty-snapshot guard.
     * @private
     */
    _notifyFixedLayoutPrintedPages() {
        const pages = [];
        for (const wnd of this._attachedFrameWindows()) {
            const doc = wnd.document;
            if (!doc?.body) continue;

            const markers = collectPagebreakMarkers(doc);
            if (markers.length > 0) {
                const label = pagebreakPageNumber(markers[0]);
                if (label !== null && label > 0) {
                    pages.push(label);
                }
                continue;
            }

            const href = wnd.frameElement?.getAttribute('data-original-href');
            const position = this.epubNavigator?.positions?.find(pos => pos.href === href)?.locations?.position;
            if (typeof position === 'number') {
                pages.push(position);
            }
        }

        if (pages.length === 0) return;

        this.onFixedLayoutPagesChanged([...new Set(pages)].sort((a, b) => a - b));
    }

    /**
     * Re-lays the book out after the host moved the reader's edges (sidebar
     * open, close or drag). The queued resize returns a reflowable book to its
     * reading anchor, taken when the move started (see markReflowStart); a
     * fixed-layout book returns to the leftmost page of its spread.
     */
    triggerResize() {
        return this.epubNavigator?.resizeHandler();
    }

    _fixedLayoutResizeTarget() {
        const positions = this.epubNavigator?.positions ?? [];
        return (
            this._attachedFrameWindows()
                .map(wnd => wnd.frameElement?.getAttribute('data-original-href'))
                .map(href => positions.find(position => position.href === href))
                .filter(Boolean)
                .sort((left, right) => left.locations.position - right.locations.position)[0] ?? null
        );
    }

    /**
     * Increases the font size by FONT_SIZE_STEP, up to FONT_SIZE_MAX.
     * @returns {number} The new font size in rem
     */
    zoomIn() {
        if (this.currentFontSize >= FONT_SIZE_MAX) {
            return this.currentFontSize;
        }
        this.currentFontSize = Math.min(FONT_SIZE_MAX, this.currentFontSize + FONT_SIZE_STEP);
        sendBreadcrumb('layout', 'zoom_in', { zoom: this.currentFontSize });
        this._submitFontSizePreferences();
        return this.currentFontSize;
    }

    /**
     * Decreases the font size by FONT_SIZE_STEP, down to FONT_SIZE_MIN.
     * @returns {number} The new font size in rem
     */
    zoomOut() {
        if (this.currentFontSize <= FONT_SIZE_MIN) {
            return this.currentFontSize;
        }
        this.currentFontSize = Math.max(FONT_SIZE_MIN, this.currentFontSize - FONT_SIZE_STEP);
        sendBreadcrumb('layout', 'zoom_out', { zoom: this.currentFontSize });
        this._submitFontSizePreferences();
        return this.currentFontSize;
    }

    /**
     * Resets the font size to FONT_SIZE_DEFAULT (100%).
     * @returns {number} The default font size in rem
     */
    zoomReset() {
        this.currentFontSize = FONT_SIZE_DEFAULT;
        sendBreadcrumb('layout', 'zoom_reset', { zoom: this.currentFontSize });
        this._submitFontSizePreferences();
        return this.currentFontSize;
    }

    handleEndOfChapter(locator, method = 'unknown') {
        if (this.isChapterEnd) {
            return;
        }

        this.isChapterEnd = true;

        sendBreadcrumb('nav', 'end_of_chapter', {
            chapterHref: locator?.href,
            method,
        });

        if (locator.locations.position !== this.epubNavigator.positions.length) {
            this.onEndOfChapter();
        }
    }

    handleStartOfChapter(locator, method = 'unknown') {
        if (this.isChapterStart) {
            return;
        }

        this.isChapterStart = true;

        sendBreadcrumb('nav', 'start_of_chapter', {
            chapterHref: locator?.href,
            method,
        });

        if (locator.locations.position !== 1) {
            this.onStartOfChapter();
        }
    }

    /**
     * Detects chapter boundaries by comparing adjacent positions.
     *
     * Paged mode only. A position is a fixed amount of text, not a screen,
     * so in scroll mode the adjacent position can be several screens away
     * and this would flag the boundary with most of the chapter left to
     * scroll. Scroll mode reads the frame's real scroll offset instead
     * (see the scroll listener).
     */
    checkChapterBoundaries(currentLocation) {
        if (!this.epubNavigator || this.scrollPageMode) {
            return;
        }

        try {
            if (currentLocation) {
                if (
                    currentLocation.locations &&
                    currentLocation.locations.position !== undefined &&
                    currentLocation.locations.totalProgression !== undefined
                ) {
                    const currentPosition = currentLocation.locations.position;
                    const nextPosition = this.epubNavigator.positions[currentPosition];

                    if (currentLocation.href !== nextPosition?.href) {
                        this.handleEndOfChapter(currentLocation, 'position');
                    }

                    const prevPosition = this.epubNavigator.positions[currentPosition - 2];

                    if (currentLocation.href !== prevPosition?.href) {
                        this.handleStartOfChapter(currentLocation, 'position');
                    }
                }
            }
        } catch (error) {
            console.warn('Error checking chapter boundaries:', error);
        }
    }

    handlePositionChange(locator) {
        if (!locator || !locator.href) {
            return;
        }

        const currentChapterHref = locator.href;

        sendBreadcrumb('nav', 'position_changed', {
            chapterHref: currentChapterHref,
            progression: locator.locations?.progression,
            position: locator.locations?.position,
        });

        if (this.currentChapterHref !== currentChapterHref) {
            sendBreadcrumb('nav', 'chapter_changed', {
                from: this.currentChapterHref,
                to: currentChapterHref,
            });
            this.onChapterChange(currentChapterHref, this.currentChapterHref);
            this.currentChapterHref = currentChapterHref;
        }

        this.isChapterEnd = false;
        this.isChapterStart = false;

        this.checkChapterBoundaries(locator);

        // A chapter that fits one screen sits at both boundaries at once and never
        // scrolls, so the `scroll` listener never reports it. In paged mode this
        // also catches a last page holding several positions, which the position
        // check above misses.
        const nav = this.epubNavigator;
        if (!nav) {
            return;
        }

        if (nav.isScrollEnd) {
            this.handleEndOfChapter(locator, 'scrollEnd');
        }
        if (nav.isScrollStart) {
            this.handleStartOfChapter(locator, 'scrollStart');
        }
    }

    /**
     * @R2 Backup selection handler for R2's native textSelected event.
     * Builds selectionData from the browser's DOM selection and fires onSelectionInProgress
     * to show the highlight creation popup.
     *
     * This is the secondary path — the primary one is _setupSelectionListeners (pointerup)
     * in HighlightManager, which works more reliably across all iframe configurations.
     *
     * @param {Object} selection - R2's textSelected event data (text, x, y, width, height, targetFrameSrc)
     */
    handleTextSelection(selection) {
        const targetFrame = this.findFrameBySource(selection.targetFrameSrc);
        if (!targetFrame) return;

        try {
            const frameDocument = targetFrame.contentDocument;
            const frameWindow = targetFrame.contentWindow;
            if (!frameDocument || !frameWindow) return;

            const domSelection = frameWindow.getSelection();
            if (!domSelection || domSelection.rangeCount === 0) return;

            const range = domSelection.getRangeAt(0);
            sendBreadcrumb('selection', 'text_selected', {
                chapterHref: this.highlightManager?.currentChapterHref,
                hasRange: true,
            });

            const selectionData = {
                // R2 reports the rendered text, which drops the trailing space of every
                // absolutely positioned word span in fixed-layout books; read the DOM instead.
                text: readRangeText(range),
                position: {
                    x: selection.x,
                    y: selection.y,
                    width: selection.width,
                    height: selection.height,
                },
                iframeDocument: frameDocument.defaultView.frameElement,
                range,
                startContainer: range.startContainer,
                endContainer: range.endContainer,
                startOffset: range.startOffset,
                endOffset: range.endOffset,
                commonAncestorContainer: range.commonAncestorContainer,
                parentElements: findRangeParentElements(range),
            };

            this.onSelectionInProgress(selectionData);
        } catch (error) {
            console.error('Error processing text selection:', error);
        }
    }

    findFrameBySource(targetFrameSrc) {
        const iframes = document.querySelectorAll('#readiumViewport iframe');
        for (const iframe of iframes) {
            if (iframe.src === targetFrameSrc || iframe.contentWindow?.location?.href === targetFrameSrc) {
                return iframe;
            }
        }
        return null;
    }

    /**
     * Classifies a Readium FrameClickEvent target as 'highlight' | 'image' | 'text' | 'other'.
     * `event.targetElement` is the outerHTML string of the clicked element, so detection
     * goes through the existing string-based predicates `isHighlightElement` / `isImageElement`.
     * @param {Object} event - The FrameClickEvent
     * @returns {'highlight'|'image'|'text'|'other'}
     */
    _classifyClickTarget(event) {
        if (event?.interactiveElement) return 'other';
        const targetElement = event?.targetElement;
        if (!targetElement) return 'other';
        if (this.isHighlightElement(targetElement)) return 'highlight';
        if (this.isImageElement(targetElement)) return 'image';
        return 'text';
    }

    /**
     * Handles tap events from R2's navigator on touch devices.
     * Pipeline: interactive element skip, navigation guard (no modal open),
     * image zoom, page turn (quarter-based), and UI toggle.
     * @param {Object} event - The FrameClickEvent from R2's listener system
     */
    handleTap(event) {
        sendBreadcrumb('selection', 'tap', { targetType: this._classifyClickTarget(event) });
        if (event.interactiveElement) {
            return;
        }

        if (!this.isNavigationEnabled()) {
            return;
        }

        // On mobile, a tap behind the settings backdrop dismisses it first —
        // even over an image — and never turns the page or toggles the UI.
        if (this.isMobileOverlayOpen()) {
            this.onCloseMobileOverlay();
            return;
        }

        if (this.handleImageClick(event)) {
            return;
        }

        const windows = this._attachedFrameWindows();
        if (windows.length && !this.scrollPageMode && !this._isFxlLoading()) {
            // Both pages of a spread answer taps, so the quarters span their combined width.
            const spreadWidth = windows.reduce((total, wnd) => total + wnd.innerWidth, 0);
            const oneQuarter = (spreadWidth * window.devicePixelRatio) / 4;
            if (event.x < oneQuarter) {
                this.requestPrevPage();
                return;
            } else if (event.x > oneQuarter * 3) {
                this.requestNextPage();
                return;
            }
        }

        this.onToggleUi();
    }

    /**
     * Handles click events from R2's navigator on mouse/pointer devices.
     * Pipeline: interactive element skip, navigation guard (no modal open),
     * image zoom, and UI toggle.
     * @param {Object} event - The FrameClickEvent from R2's listener system
     */
    handleClick(event) {
        sendBreadcrumb('selection', 'click', { targetType: this._classifyClickTarget(event) });
        if (event.interactiveElement) {
            return;
        }

        if (!this.isNavigationEnabled()) {
            return;
        }

        // On mobile, a click behind the settings backdrop dismisses it first —
        // even over an image — and never toggles the UI.
        if (this.isMobileOverlayOpen()) {
            this.onCloseMobileOverlay();
            return;
        }

        if (this.handleImageClick(event)) {
            return;
        }

        this.onToggleUi();
    }

    requestNextPage() {
        this._requestPage('go_next', this.onGoNext);
    }

    requestPrevPage() {
        this._requestPage('go_prev', this.onGoPrev);
    }

    /**
     * Hands the turn to Vue, whose page actions move the navigator and
     * apply the gates that hold or end paging.
     */
    _requestPage(breadcrumb, turnPage) {
        const loc = this.getCurrentLocation();
        sendBreadcrumb('nav', breadcrumb, {
            chapterHref: loc?.href,
            progression: loc?.locations?.progression,
        });
        turnPage();
    }

    /**
     * Navigate to a specific locator (R2 Locator object)
     * Used by store.js goToLocation action
     *
     * @param {Object} locator - R2 Locator object with href, locations, etc.
     */
    goToLocator(locator) {
        if (!locator) return;
        sendBreadcrumb('nav', 'go_to_locator', {
            chapterHref: locator?.href,
            progression: locator?.locations?.progression,
        });
        // A jump resolves in two steps (chapter landing at progression 0,
        // then the within-chapter adjustment) — each firing positionChanged.
        // The emit-final window collapses them into one turn at the final
        // destination; without it every jump double-counts.
        this.beginTrackingSuppression('emit-final');
        this.epubNavigator.go(locator, true, () => {
            this.endTrackingSuppression();
        });
    }

    /**
     * The locator for a printed page of this book, or null when its pages
     * cannot be placed. Readium access lives here so the store does not
     * reach into the navigator.
     *
     * @param {number} page - Printed page number
     * @param {{ spine: Array<Object>, totalPages: number }} hostPages - The host's per-chapter page ranges
     * @returns {Object|null} Plain RWPM locator data
     */
    printedPageLocator(page, hostPages) {
        const placed = this._printedPageLocatorsFor(hostPages).get(page);
        // Which of the three sources placed the page answers "why did it land
        // there" on a book whose numbering looks wrong.
        sendBreadcrumb('nav', 'printed_page_locator', { page, source: placed?.source ?? 'none' });
        return placed?.locator ?? null;
    }

    /**
     * The resource a printed page lives in, or null when its pages cannot be
     * placed. No breadcrumb: a batch preview asks for hundreds of pages at
     * once and turns to none, which would push the navigation trail out of Sentry.
     *
     * @param {number} page - Printed page number
     * @param {{ spine: Array<Object>, totalPages: number }} hostPages - The host's per-chapter page ranges
     * @returns {string|null}
     */
    printedPageHref(page, hostPages) {
        return this._printedPageLocatorsFor(hostPages).get(page)?.locator?.href ?? null;
    }

    /** Built once and kept: a viewer serves one book, and the map covers every page of it. */
    _printedPageLocatorsFor({ spine, totalPages }) {
        this._printedPageLocators ??= buildPrintedPageLocators({ publication: this.publication, spine, totalPages });
        return this._printedPageLocators;
    }

    /**
     * Turns to a printed page through its marker, then settles the screen on
     * the page's text. Readium lands on the column (paginated) or the line
     * (scroll) holding the marker, which is not always where the page's text
     * starts. A locator without a marker fragment has nothing to settle on
     * and travels like any other.
     *
     * @param {Object} locator - R2 Locator whose first fragment is the marker id
     */
    goToPrintedPage(locator) {
        const fragment = locator?.locations?.fragments?.[0];
        if (!fragment) {
            this.goToLocator(locator);
            return;
        }

        sendBreadcrumb('nav', 'go_to_printed_page', { chapterHref: locator.href, fragment });
        this.beginTrackingSuppression('emit-final');
        this.epubNavigator.go(locator, true, () => {
            // Held so `destroy()` can cancel it: the callback walks the frames,
            // and a book closed inside the settle window has none left.
            this._printedPageSettleTimer = setTimeout(() => {
                this._printedPageSettleTimer = null;
                this._settlePrintedPageLanding(fragment);
            }, COLUMN_SNAP_SETTLE_MS);
        });
    }

    /**
     * Moves the screen on to the page's own text when the marker left it
     * behind: a marker closing a column carries the page's first words into
     * the next one. The suppression window closes with the correction, so
     * the jump counts once, on the page the reader ends on.
     *
     * @private
     * @param {string} fragment - The marker id the navigator just landed on
     */
    _settlePrintedPageLanding(fragment) {
        const located = this._frameElementById(fragment);
        const pageStart = located && firstTextRangeAfter(located.element);
        const context = this._readingContext();

        if (pageStart && !beginsOnReadingScreen(located.wnd, pageStart.getBoundingClientRect(), context)) {
            if (context.scroll) {
                scrollToReadingAnchor(located.wnd, pageStart, context.edgeStart);
            } else {
                // eslint-disable-next-line no-restricted-syntax -- this finishes a printed-page jump on the page's own text, so no reader turned the page
                this.epubNavigator.goRight(false, () => this.endTrackingSuppression());
                return;
            }
        }

        this.endTrackingSuppression();
    }

    /**
     * The first chapter frame holding an element with this id, or null.
     *
     * @private
     * @param {string} id
     * @returns {{ wnd: Window, element: Element }|null}
     */
    _frameElementById(id) {
        return (
            this._attachedFrameWindows()
                .map(wnd => ({ wnd, element: wnd.document?.getElementById(id) }))
                .find(({ element }) => element) ?? null
        );
    }

    /**
     * Navigate by R2 position index (from the range paginator / progress bar).
     * Position list access lives here so the store doesn't need to reach into
     * the navigator internals.
     *
     * @param {number} index - Zero-based index into the navigator's positions
     */
    navigateToPositionIndex(index) {
        const locator = this.epubNavigator?.positions?.[index];
        if (!locator) return;
        this.goToLocator(locator);
    }

    /**
     * The resource a fixed-layout page is, on the same one-position-per-page
     * scale navigateToPositionIndex turns to.
     *
     * @param {number} page - 1-based page number
     * @returns {string|null}
     */
    fixedLayoutPageHref(page) {
        return this.epubNavigator?.positions?.[page - 1]?.href ?? null;
    }

    /**
     * Navigate to a link (R2 Link object)
     * Used for Table of Contents navigation
     *
     * @param {Object} link - R2 Link object with href
     */
    goToLink(link) {
        if (!link) return;
        const linkObj = new Link({ href: link.href || link });
        sendBreadcrumb('nav', 'go_to_link', { href: linkObj.href });
        this.beginTrackingSuppression('emit-final');
        this.epubNavigator.goLink(linkObj, true, () => {
            this.endTrackingSuppression();
        });
    }

    /**
     * Navigate to a legacy R1 location (from notes/highlights created with Readium 1).
     * Resolves the R1 idref to R2 href and uses text for precise navigation.
     *
     * @deprecated This method is scheduled for removal once a reliable bulk migration
     * strategy is implemented. Currently, renderHighlight performs lazy migration of
     * R1 notes to R2 format, but only when a chapter is visited and rendered. This
     * means notes in unvisited chapters remain unmigrated. Until we implement a
     * mechanism to migrate all R1 notes (e.g., background migration on book load or
     * server-side batch migration), this fallback navigation method must remain to
     * ensure users can navigate to unmigrated R1 notes.
     *
     * @param {Object} epubLocation - R1 location object with chapterHref and cfi
     * @param {string} [totalText] - Text of the note/highlight for precise positioning
     */
    goToR1Location(epubLocation, totalText) {
        if (!epubLocation) return;

        const chapterIdref = epubLocation.chapterHref;
        const cfi = epubLocation.cfi;

        // 1. Try to resolve idref to href using the idrefToHrefMap
        let resolvedHref = this.idrefToHrefMap?.get(chapterIdref);

        // 2. Fallback: try to resolve from readingOrder (exact/suffix/filename match)
        if (!resolvedHref) {
            resolvedHref = this._resolveChapterFromReadingOrder({ chapterHref: chapterIdref });
        }

        // 3. Last fallback: use chapterIdref directly (might work if it's already a valid href)
        if (!resolvedHref) {
            resolvedHref = chapterIdref;
        }

        // 4. Extract fragment ID from CFI as fallback (e.g., [introduction_1] from /4/2[introduction_1]/26/1:254)
        let fragmentId = null;
        if (cfi) {
            const fragmentMatch = cfi.match(/\[([^\]]+)\]/);
            fragmentId = fragmentMatch ? fragmentMatch[1] : null;
        }

        // 5. Create Locator with text for precise navigation, fragment as fallback
        const locator = new Locator({
            href: resolvedHref,
            type: 'application/xhtml+xml',
            locations: new LocatorLocations({
                fragments: fragmentId ? [fragmentId] : [],
            }),
            text: totalText ? new LocatorText({ highlight: totalText }) : undefined,
        });

        this.beginTrackingSuppression('emit-final');
        this.epubNavigator.go(locator, true, success => {
            this.endTrackingSuppression();
            if (!success) {
                console.warn('goToR1Location: navigation failed', { resolvedHref, totalText });
            }
        });
    }

    isHighlightElement(targetElement) {
        if (!targetElement || typeof targetElement !== 'string') {
            return false;
        }
        return /^<mark\b[^>]*>/i.test(targetElement.trim());
    }

    /**
     * Determines if a target element HTML string represents an image element.
     * Checks for both HTML <img> and SVG <image> tags.
     * @param {string} targetElement - The outerHTML string of the clicked element from R2's FrameClickEvent
     * @returns {boolean} Whether the element is an img or image tag
     */
    isImageElement(targetElement) {
        if (!targetElement || typeof targetElement !== 'string') {
            return false;
        }
        const trimmed = targetElement.trim();
        return /^<img\b/i.test(trimmed) || /^<image\b/i.test(trimmed);
    }

    /**
     * Resolves the full image URL and alt text from an R2 FrameClickEvent.
     * Uses the event's cssSelector to query the actual DOM element inside the iframe,
     * then resolves relative URLs against the document's baseURI.
     * @param {Object} event - The FrameClickEvent from R2's listener system
     * @returns {{ src: string, alt: string, path: string } | null} Image data or null if resolution fails
     */
    resolveImageUrlFromEvent(event) {
        try {
            if (!event.cssSelector) {
                return null;
            }

            const windows = this._attachedFrameWindows();
            const targetIframeWindow = windows.find(wnd => wnd.location?.href === event.targetFrameSrc) ?? windows[0];
            if (!targetIframeWindow) {
                return null;
            }

            const targetDomElement = targetIframeWindow.document.querySelector(event.cssSelector);
            if (!targetDomElement) {
                return null;
            }

            const originalUrl =
                targetDomElement.currentSrc ||
                targetDomElement.src ||
                (targetDomElement.href && targetDomElement.href.baseVal) ||
                (targetDomElement.getAttribute &&
                    (targetDomElement.getAttribute('href') || targetDomElement.getAttribute('xlink:href')));

            if (!originalUrl) {
                return null;
            }

            const baseUri = targetDomElement.ownerDocument?.baseURI || '';
            let resolvedUrl = originalUrl;
            try {
                resolvedUrl = /^(https?:|blob:|data:)/i.test(originalUrl)
                    ? originalUrl
                    : new URL(originalUrl, baseUri).toString();
            } catch (_) {}

            const altText = targetDomElement.alt || targetDomElement.href?.baseVal || '';

            // The resolved URL carries the session's token base, so it is no
            // anchor: an add-on has to name the figure by its path from the
            // package root, which outlives the token.
            const path = this._extractRelativeHref(resolvedUrl);

            return { src: resolvedUrl, alt: altText, path: path === resolvedUrl ? '' : path };
        } catch (error) {
            console.warn('[R2] Error resolving image URL from click event:', error);
            return null;
        }
    }

    /**
     * Checks if a click/tap event targeted an image element and invokes the image click callback.
     * @param {Object} event - The FrameClickEvent from R2's listener system
     * @returns {boolean} Whether an image click was detected and handled
     */
    handleImageClick(event) {
        if (!this.isImageElement(event.targetElement)) {
            return false;
        }

        const imageData = this.resolveImageUrlFromEvent(event);
        if (!imageData) {
            return false;
        }

        this.onImageClick(imageData);
        return true;
    }

    /**
     * Navigates to a fragment inside the current chapter, the explicit
     * "go to note" action from the footnote panel. Uses the same-document
     * navigation R2 performs for interactive `#fragment` anchors, which
     * the footnote guard suppresses for noteref clicks.
     * @param {string} fragment - The note element's id
     */
    goToNoteDestination(fragment) {
        const nav = this.epubNavigator;
        if (!nav?.currentLocation || !fragment) return;
        sendBreadcrumb('footnote', 'go_to_note_destination', { fragment });
        nav.go(nav.currentLocation.copyWithLocations({ fragments: [fragment] }), true, () => {
            this._flashNoteDestination(fragment);
        });
    }

    /**
     * Briefly highlights the note the reader just jumped to, so the eye can
     * find the landing spot. The flash fades out on its own (see the
     * volpe-footnote-target keyframes injected with the hit-area styles).
     * @param {string} fragment - The note element's id
     */
    _flashNoteDestination(fragment) {
        setTimeout(() => {
            const target = this._frameElementById(fragment)?.element;
            if (!target) return;

            target.classList.remove('volpe-footnote-target');
            // Restart the animation when jumping to the same note twice.
            void target.offsetWidth;
            target.classList.add('volpe-footnote-target');
            setTimeout(() => target.classList.remove('volpe-footnote-target'), 2500);
        }, COLUMN_SNAP_SETTLE_MS);
    }

    /**
     * Stops R2 from navigating a noteref gesture that opens the panel: its
     * injectables report every tap/click through FrameComms, and the
     * navigator then follows `#fragment` anchors by itself. The decision
     * comes from that report alone, which names the gesture's anchor, so it
     * does not depend on whether the report arrives before or after the DOM
     * click opens the panel. It runs the click listener's criterion on the
     * same live anchor, so a swallowed navigation always comes with the
     * preventDefault() that stops the browser's native jump. The wrap only
     * works because every FrameComms wiring dispatches through an arrow,
     * never a bound reference.
     * @param {Object} nav - The EpubNavigator instance
     */
    _setupFootnoteNavigationGuard(nav) {
        if (typeof nav?.eventListener !== 'function') return;

        const originalEventListener = nav.eventListener.bind(nav);
        // Rest-forwarded: the navigator passes the emitting frame as a third argument, and
        // dropping it costs the frame-scoped branches downstream (decoration re-application,
        // the selection Locator).
        nav.eventListener = (key, value, ...rest) => {
            if ((key === 'click' || key === 'tap') && this._isPanelNoterefReport(value, rest[0])) {
                sendBreadcrumb('footnote', 'r2_link_navigation_swallowed');
                return;
            }
            return originalEventListener(key, value, ...rest);
        };
    }

    /**
     * Whether an R2 tap/click report names a noteref the panel opens.
     * @param {Object} report - The FrameClickEvent R2 reports
     * @param {Object} [sourceFrame] - The frame manager that emitted it
     * @returns {boolean}
     */
    _isPanelNoterefReport(report, sourceFrame) {
        try {
            const doc = sourceFrame?.iframe?.contentDocument;
            if (!doc) return false;

            const anchor = findReportedAnchor(report?.interactiveElement, doc);
            return !!anchor && this._resolveFootnoteGesture(anchor, doc).kind === 'note';
        } catch (error) {
            console.warn('[R2] Error reading footnote report:', error);
            return false;
        }
    }

    /**
     * Refuses a `go()` whose target has no entry in the position list.
     *
     * R2 sets `currentLocation` to `positions.find(...)` unchecked, so a
     * reading-order item the host's list does not cover leaves it undefined,
     * `apply()` throws and the `_isNavigating` latch never resets. `goLink()`
     * delegates to `go`, so wrapping the instance method covers both. Targets
     * outside the reading order stay with R2 (`handleLocator`).
     * @param {Object} nav - The EpubNavigator instance
     */
    _setupNavigationTargetGuard(nav) {
        if (typeof nav?.go !== 'function') return;

        const originalGo = nav.go.bind(nav);
        nav.go = (locator, animated, callback) => {
            const link = nav.publication.readingOrder.findWithHref(locator.href.split('#')[0]);
            // Before load() the list is not there yet; R2 keeps its own behavior then.
            if (link && nav.positions && !nav.positions.some(position => position.href === link.href)) {
                sendBreadcrumb('nav', 'go_refused_no_position', { href: link.href }, 'warning');
                callback(false);
                return;
            }
            return originalGo(locator, animated, callback);
        };
    }

    /**
     * Every chapter-frame window currently alive.
     *
     * The DOM is the source of truth: on cold load the pool's frame list is still sparse
     * while the initial chapter iframe is already attached. The navigator's frames are
     * merged in on top because `frameLoaded` fires only for `currentFrames[0]` — a
     * pool-preloaded chapter, or the second frame of a spread, never passes through it and
     * would otherwise be invisible to anything keyed on that event.
     *
     * Must not throw. Both callers run from R2 callbacks where an escaping error cuts the
     * rest of the callback short, and the navigator's frame members are getters that throw
     * once the pool has destroyed the frame — optional chaining is no defence there. The
     * reads go through `frameAccess`, which answers null instead (FUL-561).
     *
     * @private
     * @returns {Set<Window>}
     */
    _contentFrameWindows() {
        const windows = new Set();

        (this.container?.querySelectorAll('iframe') || []).forEach(iframe => {
            if (iframe.contentWindow) windows.add(iframe.contentWindow);
        });

        this._attachedFrameWindows().forEach(wnd => windows.add(wnd));

        return windows;
    }

    /**
     * Installs the frame layers on every frame the pool currently holds.
     * Safe to call on every positionChanged: each layer skips a document it
     * already holds (see _setupFootnoteInteraction and _onFrameKeydown).
     *
     * Must not throw: it runs from a MutationObserver and from R2's
     * positionChanged callback, where an escaping error becomes an unhandled
     * error and cuts the rest of the callback short. The frame lookup answers
     * null for a destroyed frame (see _contentFrameWindows) and the install is
     * caught per window, so nothing here escapes.
     */
    _ensureFrameLayers() {
        // The pool also swaps the document inside a reused iframe node, which
        // no childList mutation reports, so re-install on each load.
        (this.container?.querySelectorAll('iframe') || []).forEach(iframe => {
            if (!iframe.dataset.volpeFrameLayersWatched) {
                iframe.dataset.volpeFrameLayersWatched = 'true';
                iframe.addEventListener('load', () => {
                    if (iframe.contentWindow) this._installFrameLayers(iframe.contentWindow);
                });
            }
        });

        this._contentFrameWindows().forEach(wnd => {
            try {
                this._installFrameLayers(wnd);
            } catch (error) {
                console.warn('[R2] Error installing frame layers:', error);
            }
        });
    }

    /**
     * Installs the footnote layer and the arrow-key listener on a chapter document.
     * It listens on keydown because a turn moves focus into the new frame, so a
     * keyup there can belong to a press Reader.vue's listener already answered.
     * @param {Window} wnd - The chapter iframe's contentWindow
     */
    _installFrameLayers(wnd) {
        this._setupFootnoteInteraction(wnd);
        wnd?.document?.addEventListener('keydown', this._onFrameKeydown);
    }

    /**
     * Installs the footnote interaction layer on a chapter document:
     * hit-area CSS plus a capture-phase click listener that opens the panel
     * and stops the browser's native fragment jump. R2's own navigation to
     * the note is stopped by the navigation guard. Cross-file notes fall
     * through untouched so R2's own navigation lands on the note.
     * @param {Window} _wnd - The chapter iframe's contentWindow
     */
    _setupFootnoteInteraction(_wnd) {
        const doc = _wnd?.document;
        if (!doc?.head || doc.querySelector('style[data-volpe="footnote-hit-area"]')) return;

        const style = doc.createElement('style');
        style.setAttribute('data-volpe', 'footnote-hit-area');
        style.textContent = FOOTNOTE_CONTENT_STYLES;
        doc.head.appendChild(style);

        doc.addEventListener(
            'click',
            e => {
                try {
                    const result = this._resolveFootnoteGesture(e.target, doc);
                    if (result.kind === 'none' || result.kind === 'cross-file') {
                        if (result.kind === 'cross-file') {
                            sendBreadcrumb('footnote', 'noteref_cross_file_jump');
                        }
                        return;
                    }

                    e.preventDefault();
                    e.stopPropagation();
                    sendBreadcrumb('footnote', 'noteref_panel_open', { label: result.label });

                    this.onFootnoteClick({
                        label: result.label,
                        html: result.html,
                        text: result.text,
                        anchor: result.anchor,
                        fragment: result.fragment,
                        anchorRect: result.anchor.getBoundingClientRect(),
                        iframeRect: _wnd.frameElement?.getBoundingClientRect() ?? null,
                    });
                } catch (error) {
                    console.warn('[R2] Error handling footnote click:', error);
                }
            },
            { capture: true }
        );
    }

    /**
     * Resolves the note a gesture on `target` opens in the panel. Fixed-layout
     * books never open it, so their gestures resolve to `none`.
     * @param {Element|null} target - The gesture's event target
     * @param {Document} doc - The chapter document
     * @returns {ReturnType<typeof resolveFootnoteClick>}
     */
    _resolveFootnoteGesture(target, doc) {
        if (this.isFixedLayout()) return { kind: 'none' };
        return resolveFootnoteClick(target, doc);
    }

    /**
     * Re-collects the note-marker anchors whenever the reader container or its
     * parent changes size (sidebars opening/closing, window resizes). The anchors
     * hold parent-viewport coordinates, which those layout shifts invalidate
     * without firing any navigator event.
     * @param {HTMLElement} container - The navigator's container element
     */
    /**
     * Leaves a breadcrumb with the current chapter frame's text metrics on
     * every orientation change, so a "the text grew after rotating" report can
     * be read against what the browser did (computed font size, zoom,
     * text-size-adjust) versus what the reader asked for (the font-size
     * setting and the --USER__fontSize it maps to).
     */
    _observeOrientation() {
        if (typeof window.matchMedia !== 'function') return;
        this._orientationQuery?.removeEventListener?.('change', this._onOrientationChange);
        this._orientationQuery = window.matchMedia('(orientation: portrait)');
        this._onOrientationChange = () => this._reportFrameTextMetrics('orientation_changed');
        this._orientationQuery.addEventListener?.('change', this._onOrientationChange);
    }

    _reportFrameTextMetrics(message) {
        try {
            const frameWindow = this._currentFrameWindow();
            const doc = frameWindow?.document;
            if (!doc?.body) return;
            const bodyStyle = frameWindow.getComputedStyle(doc.body);
            const paragraph = doc.querySelector('body p');
            sendBreadcrumb('layout', message, {
                portrait: this._orientationQuery?.matches ?? null,
                frameViewport: `${frameWindow.innerWidth}x${frameWindow.innerHeight}`,
                fontSizeSetting: this.currentFontSize,
                userFontSize: doc.documentElement.style.getPropertyValue('--USER__fontSize') || null,
                bodyZoom: bodyStyle.zoom,
                bodyTextSizeAdjust: bodyStyle.webkitTextSizeAdjust || bodyStyle.textSizeAdjust || null,
                paragraphFontSize: paragraph ? frameWindow.getComputedStyle(paragraph).fontSize : null,
            });
        } catch {
            // Diagnostics only: a frame mid-swap must never break a rotation.
        }
    }

    _observeContainerResize(container) {
        if (typeof ResizeObserver === 'undefined' || !container) return;
        this._containerResizeObserver?.disconnect();
        this._containerResizeObserver = new ResizeObserver(() => this.notifyNoteMarkerAnchors());
        this._containerResizeObserver.observe(container);
        if (container.parentElement) this._containerResizeObserver.observe(container.parentElement);
    }

    /**
     * Recollects the note-marker anchors and notifies Vue via onNoteMarkersChanged.
     * Throttled to one collection per animation frame — it's wired to high-frequency
     * triggers (scroll, position changes, container resizes) besides the marker
     * render events. By design, Fixed Layout EPUBs show no note markers: the
     * anchor geometry assumes ReadiumCSS text columns, which fixed layout does
     * not use, so markers are intentionally disabled for that layout.
     */
    notifyNoteMarkerAnchors() {
        if (this.isFixedLayout()) return;
        if (this._noteMarkersRafId) return;
        this._noteMarkersRafId = requestAnimationFrame(() => {
            this._noteMarkersRafId = null;
            this.onNoteMarkersChanged(this.highlightManager.collectNoteMarkerAnchors());
        });
    }

    /**
     * Routes a note marker click through the same path as a click on the
     * highlight's own mark, so the note opens with identical behavior.
     * @param {string} uuid - The highlight's uuid
     */
    selectHighlightByUuid(uuid) {
        const highlight = this.highlightManager.currentChapterHighlights[uuid];
        if (highlight) this.onSelectHighlight(highlight);
    }

    initHighlights(getSelectionsState, context) {
        // @R2 Pass current content frames so HighlightManager can find the visible body
        this.highlightManager.getContentFrames = () => attachedFrames(this.epubNavigator);
        // @R2 Pass function to extract relative href from full URL for navigation
        this.highlightManager.getRelativeHref = fullHref => this._extractRelativeHref(fullHref);
        this.highlightManager.initHighlights(getSelectionsState, context);
    }

    updateCurrentChapterHighlights(selections, context) {
        this.highlightManager.updateCurrentChapterHighlights(selections, context);
    }

    renderHighlights() {
        this.highlightManager.renderHighlights();
    }

    renderHighlight(highlight) {
        this.highlightManager.renderHighlight(highlight);
    }

    renderHighlightInPage(context, startOffset, length, className, color, highlight) {
        this.highlightManager.renderHighlightInPage(
            context,
            startOffset,
            length,
            className,
            color,
            highlight,
            this.onSelectHighlight
        );
    }

    renderHighlightInPageWhenIsSearching(context, queryWords, frameIsLoading) {
        this.highlightManager.renderHighlightInPageWhenIsSearching(context, queryWords, frameIsLoading);
    }

    applyColorToMarker(mark, color) {
        this.highlightManager.applyColorToMarker(mark, color);
    }

    clearMarkedSelection(className, context) {
        this.highlightManager.clearMarkedSelection(className, context);
    }

    async saveHighlight(params, selection, anonymousSession) {
        await this.highlightManager.saveHighlight(params, selection, anonymousSession);
    }

    removeHighlight(selectionUniqueCssClass, context) {
        this.highlightManager.removeHighlight(selectionUniqueCssClass, context);
    }

    /**
     * Paints a newly created flashcard's marker. No-ops when the flashcard has no anchor or isn't
     * in a live frame (handled by HighlightManager.renderFlashcard).
     * @param {Object} flashcard - The created flashcard model instance
     */
    renderFlashcardMarker(flashcard) {
        this.highlightManager.renderFlashcard(flashcard);
    }

    /**
     * Repaints a recolored flashcard's marker (unmark + re-render with the new color).
     * @param {Object} flashcard - The recolored flashcard model instance
     */
    repaintFlashcardMarker(flashcard) {
        this.highlightManager.repaintFlashcard(flashcard);
    }

    /**
     * Removes a deleted flashcard's marker from the current chapter.
     * @param {string} uuid - The deleted flashcard's uuid
     */
    removeFlashcardMarker(uuid) {
        this.highlightManager.removeFlashcard(uuid);
    }

    getCurrentChapterHighlights() {
        return this.highlightManager.getCurrentChapterHighlights();
    }

    setCurrentChapterHighlights(highlights) {
        this.highlightManager.setCurrentChapterHighlights(highlights);
    }

    resetInitHighlightsRetryCount() {
        this.highlightManager.resetInitHighlightsRetryCount();
    }

    clearMarkerSearchTimeout() {
        this.highlightManager.clearMarkerSearchTimeout();
    }

    destroy() {
        const hadPagebreakTracker = !!this._pagebreakTracker;
        const hadHighlightManager = !!this.highlightManager;
        const idpfBlobUrlsRevoked = this.idpfFontBlobUrls?.size ?? 0;

        this._pagebreakTracker?.destroy();
        this._pagebreakTracker = null;
        this.highlightManager?.destroy();

        this._frameLayerObserver?.disconnect();
        this._frameLayerObserver = null;

        if (this._suppressionEscapeTimer) {
            clearTimeout(this._suppressionEscapeTimer);
            this._suppressionEscapeTimer = null;
        }
        if (this._printedPageSettleTimer) {
            clearTimeout(this._printedPageSettleTimer);
            this._printedPageSettleTimer = null;
        }
        this._suppressionDepth = 0;
        this._anchorKeeper.dispose();
        for (const [doc, listener] of this._readingInputDocuments) {
            for (const event of ['wheel', 'touchmove', 'keydown']) doc.removeEventListener(event, listener, true);
        }
        this._readingInputDocuments.clear();

        if (this._noteMarkersRafId) {
            cancelAnimationFrame(this._noteMarkersRafId);
            this._noteMarkersRafId = null;
        }
        this._containerResizeObserver?.disconnect();
        this._containerResizeObserver = null;
        this._orientationQuery?.removeEventListener?.('change', this._onOrientationChange);
        this._orientationQuery = null;
        this._onOrientationChange = null;

        // ─── IDPF Font Deobfuscation cleanup ───────────────────────────────
        // The IDPF task may still be in-flight (we no longer await it during
        // init). Fire-and-forget the revoke so blob URLs are released once
        // the deobfuscation settles, regardless of whether destroy() runs
        // before or after.
        this._idpfFontsTask
            ?.then(map => {
                for (const url of map.values()) URL.revokeObjectURL(url);
            })
            .catch(() => {
                /* logged at init-time via breadcrumb */
            });
        this._idpfFontsTask = null;
        this.idpfFontBlobUrls = null;
        // ───────────────────────────────────────────────────────────────────

        sendBreadcrumb('lifecycle', 'epub_viewer_destroyed', {
            hadPagebreakTracker,
            hadHighlightManager,
            idpfBlobUrlsRevoked,
        });
    }
}
